import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { API_URL } from '../config/api';
import { 
  LayoutDashboard, Users, Package, LogOut, Settings,
  TrendingUp, DollarSign, UserCheck, Search,
  ChevronRight, ChevronLeft, ChevronDown, ChevronUp, RefreshCw, AlertCircle,
  Mail, Phone, CreditCard, Calendar, ExternalLink, ArrowUpDown, X,
  Plus, Edit2, Trash2, Eye, Receipt, CheckCircle, Clock, XCircle, User, FileText
} from 'lucide-react';

const LOGOS = {
  dark: 'https://customer-assets.emergentagent.com/job_store-boost-1/artifacts/p00q8cnd_logo_darkversion.6219654a445e56ed3cd0.png'
};

// ==================== SKELETON COMPONENTS ====================

const SkeletonPulse = ({ className }) => (
  <div className={`animate-pulse bg-slate-700/50 rounded ${className}`}></div>
);

const SkeletonStatCard = () => (
  <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-5">
    <div className="flex items-start justify-between">
      <div className="flex-1">
        <SkeletonPulse className="h-4 w-20 mb-2" />
        <SkeletonPulse className="h-8 w-16 mb-1" />
        <SkeletonPulse className="h-3 w-24" />
      </div>
      <SkeletonPulse className="w-10 h-10 rounded-lg" />
    </div>
  </div>
);

const SkeletonCard = () => (
  <div className="p-3 bg-slate-900/50 rounded-lg border border-slate-700/50">
    <div className="flex items-start justify-between mb-2">
      <SkeletonPulse className="h-4 w-32" />
      <SkeletonPulse className="h-5 w-16 rounded-full" />
    </div>
    <SkeletonPulse className="h-3 w-40 mb-1" />
    <SkeletonPulse className="h-3 w-28" />
  </div>
);

const SkeletonTableRow = () => (
  <tr className="border-b border-slate-700/50">
    <td className="p-4"><SkeletonPulse className="h-4 w-32" /><SkeletonPulse className="h-3 w-40 mt-1" /></td>
    <td className="p-4"><SkeletonPulse className="h-5 w-16 rounded-full" /></td>
    <td className="p-4"><SkeletonPulse className="h-4 w-24" /></td>
    <td className="p-4"><SkeletonPulse className="h-4 w-20" /></td>
    <td className="p-4"><SkeletonPulse className="h-4 w-16" /></td>
    <td className="p-4"><SkeletonPulse className="h-4 w-20" /></td>
  </tr>
);

// ==================== COMPONENTS ====================

const StatCard = ({ icon: Icon, label, value, color, subtext, onClick }) => (
  <div 
    className={`bg-slate-800/50 border border-slate-700 rounded-xl p-5 ${onClick ? 'cursor-pointer hover:bg-slate-800/70 transition-colors' : ''}`}
    onClick={onClick}
  >
    <div className="flex items-start justify-between">
      <div>
        <p className="text-slate-400 text-sm mb-1">{label}</p>
        <p className="text-2xl font-bold text-white">{value}</p>
        {subtext && <p className="text-xs text-slate-500 mt-1">{subtext}</p>}
      </div>
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${color}`}>
        <Icon className="w-5 h-5 text-white" />
      </div>
    </div>
  </div>
);

const StatusBadge = ({ status }) => {
  const styles = {
    active: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
    canceled: 'bg-red-500/20 text-red-400 border-red-500/30',
    past_due: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
    suspended: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
    trialing: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    pending: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
    completed: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30'
  };
  
  const labels = {
    active: 'Attivo', canceled: 'Cancellato', past_due: 'Scaduto',
    suspended: 'Sospeso', trialing: 'Trial', pending: 'Da fare', completed: 'Completata'
  };
  
  return (
    <span className={`px-2.5 py-1 rounded-full text-xs font-medium border ${styles[status] || 'bg-slate-500/20 text-slate-400 border-slate-500/30'}`}>
      {labels[status] || status || 'N/A'}
    </span>
  );
};

const SortIcon = ({ field, sortField, sortDirection }) => {
  if (sortField !== field) return <ArrowUpDown className="w-3 h-3 ml-1 opacity-30" />;
  return sortDirection === 'asc' ? <ChevronUp className="w-3 h-3 ml-1" /> : <ChevronDown className="w-3 h-3 ml-1" />;
};

// Modal Component
const Modal = ({ isOpen, onClose, title, children, size = 'md' }) => {
  if (!isOpen) return null;
  const sizeClasses = { sm: 'max-w-md', md: 'max-w-2xl', lg: 'max-w-4xl', xl: 'max-w-5xl' };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div className={`bg-slate-900 border border-slate-700 rounded-2xl w-full ${sizeClasses[size]} max-h-[90vh] overflow-hidden flex flex-col`}>
        <div className="flex items-center justify-between p-4 border-b border-slate-700">
          <h3 className="text-lg font-bold text-white">{title}</h3>
          <button onClick={onClose} className="p-2 hover:bg-slate-800 rounded-lg"><X className="w-5 h-5 text-slate-400" /></button>
        </div>
        <div className="p-4 overflow-y-auto flex-1">{children}</div>
      </div>
    </div>
  );
};

// Form Input Component
const FormInput = ({ label, type = 'text', value, onChange, placeholder, required, options, error }) => (
  <div className="space-y-1">
    <label className="text-sm text-slate-400">{label} {required && <span className="text-red-400">*</span>}</label>
    {type === 'select' ? (
      <select value={value} onChange={onChange} className={`w-full bg-slate-800 border rounded-lg px-3 py-2 text-white focus:outline-none ${error ? 'border-red-500 focus:border-red-400' : 'border-slate-700 focus:border-emerald-500'}`}>
        {options?.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
      </select>
    ) : type === 'textarea' ? (
      <textarea value={value} onChange={onChange} placeholder={placeholder} rows={3} className={`w-full bg-slate-800 border rounded-lg px-3 py-2 text-white focus:outline-none resize-none ${error ? 'border-red-500 focus:border-red-400' : 'border-slate-700 focus:border-emerald-500'}`} />
    ) : (
      <input type={type} value={value} onChange={onChange} placeholder={placeholder} className={`w-full bg-slate-800 border rounded-lg px-3 py-2 text-white focus:outline-none ${error ? 'border-red-500 focus:border-red-400' : 'border-slate-700 focus:border-emerald-500'}`} />
    )}
    {error && <p className="text-red-400 text-xs">{error}</p>}
  </div>
);

// ==================== MAIN COMPONENT ====================

// Valid tab IDs mapping
const TAB_ROUTES = {
  'dashboard': 'dashboard',
  'stripe-customers': 'clienti-stripe',
  'stripe-transactions': 'transazioni-stripe',
  'manual-customers': 'clienti-manuali',
  'configurations': 'configurazioni',
  'products': 'prodotti'
};

const ROUTE_TO_TAB = {
  'dashboard': 'dashboard',
  'clienti-stripe': 'stripe-customers',
  'transazioni-stripe': 'stripe-transactions',
  'clienti-manuali': 'manual-customers',
  'configurazioni': 'configurations',
  'prodotti': 'products'
};

const AdminDashboard = () => {
  const { user, logout, getToken, isAdmin } = useAuth();
  const navigate = useNavigate();
  const { tab } = useParams();
  
  // Derive activeTab from URL parameter
  const activeTab = ROUTE_TO_TAB[tab] || 'dashboard';
  
  // Function to change tab (updates URL)
  const setActiveTab = (tabId) => {
    const route = TAB_ROUTES[tabId] || 'dashboard';
    navigate(`/admin/${route}`);
  };
  
  // Data states
  const [dashboardStats, setDashboardStats] = useState(null);
  const [recentItems, setRecentItems] = useState(null);
  const [stripeCustomers, setStripeCustomers] = useState([]);
  const [stripeCustomersLocal, setStripeCustomersLocal] = useState({}); // Local Firebase data for Stripe customers
  const [customerTotals, setCustomerTotals] = useState({}); // Totale speso per cliente (lazy loaded)
  const [loadingTotals, setLoadingTotals] = useState(false); // Indica se sta caricando i totali
  const [stripeTransactions, setStripeTransactions] = useState([]);
  const [manualCustomers, setManualCustomers] = useState([]);
  const [configurations, setConfigurations] = useState([]);
  const [products, setProducts] = useState([]);
  const [firebaseProducts, setFirebaseProducts] = useState([]); // Products synced to Firebase for manual customers
  
  // Transactions pagination state (server-side)
  const [transactionsPagination, setTransactionsPagination] = useState({
    page: 1,
    hasMore: true,
    nextCursor: null,
    loading: false,
    searchMode: false
  });
  const [transactionsSearchTerm, setTransactionsSearchTerm] = useState('');
  
  // Stripe customer edit modal
  const [showStripeCustomerEditModal, setShowStripeCustomerEditModal] = useState(false);
  const [editingStripeCustomer, setEditingStripeCustomer] = useState(null);
  const [stripeCustomerFormData, setStripeCustomerFormData] = useState({ company_name: '' });
  const [savingStripeCustomer, setSavingStripeCustomer] = useState(false);
  const [syncingStripeCustomers, setSyncingStripeCustomers] = useState(false);
  
  // UI states
  const [loading, setLoading] = useState(true);
  const [loadingTab, setLoadingTab] = useState(false); // Loading state for tab-specific data
  const [loadingProgress, setLoadingProgress] = useState(0); // 0-100 percentage
  const [initialLoadComplete, setInitialLoadComplete] = useState(false); // After first load, show skeleton instead
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [productFilter, setProductFilter] = useState(''); // Filter by product for manual customers
  const [planTypeFilter, setPlanTypeFilter] = useState(''); // Filter by plan type (monthly/yearly/one_time)
  const [sortField, setSortField] = useState('created');
  const [sortDirection, setSortDirection] = useState('desc');
  
  // Loading states for each data section (for progressive loading)
  const [loadedSections, setLoadedSections] = useState({
    dashboard: false,
    stripeCustomers: false,
    stripeTransactions: false,
    manualCustomers: false,
    configurations: false,
    products: false,
    firebaseProducts: false
  });
  
  // Detail panel states (for Stripe customers - right sidebar)
  const [selectedStripeCustomer, setSelectedStripeCustomer] = useState(null);
  const [stripeCustomerDetail, setStripeCustomerDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  
  // Detail panel states (for Stripe transactions - right sidebar)
  const [selectedStripeTransaction, setSelectedStripeTransaction] = useState(null);
  const [stripeTransactionDetail, setStripeTransactionDetail] = useState(null);
  const [loadingTransactionDetail, setLoadingTransactionDetail] = useState(false);
  
  // Detail panel states (for Manual customers - right sidebar)
  const [selectedManualCustomer, setSelectedManualCustomer] = useState(null);
  const [manualCustomerDetail, setManualCustomerDetail] = useState(null);
  const [loadingManualDetail, setLoadingManualDetail] = useState(false);
  
  // Modal states for manual operations
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [showTransactionModal, setShowTransactionModal] = useState(false);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({});
  
  // Config Wizard states
  const [configStep, setConfigStep] = useState(1);
  const [selectedModules, setSelectedModules] = useState([]);
  const [hasVat, setHasVat] = useState(false);
  const [openingHours, setOpeningHours] = useState([]);
  const [configStaff, setConfigStaff] = useState([]);
  const [configServices, setConfigServices] = useState([]);
  const [configProducts, setConfigProducts] = useState([]);
  const [configCourses, setConfigCourses] = useState([]);
  const [configEvents, setConfigEvents] = useState([]);
  const [configSubscriptions, setConfigSubscriptions] = useState([]);
  const [wizardErrors, setWizardErrors] = useState({});
  // Loading states for CRUD operations
  const [savingCustomer, setSavingCustomer] = useState(false);
  const [savingTransaction, setSavingTransaction] = useState(false);
  const [savingInlineEdit, setSavingInlineEdit] = useState(false);
  const [deletingCustomer, setDeletingCustomer] = useState(false);
  const [deletingTransaction, setDeletingTransaction] = useState(null); // holds transaction id being deleted
  const [savingConfig, setSavingConfig] = useState(false);
  const [syncingProducts, setSyncingProducts] = useState(false);

  // ==================== DATA LOADING ====================
  
  const fetchWithAuth = useCallback(async (endpoint) => {
    const token = await getToken();
    if (!token) throw new Error('Token non disponibile');
    const res = await fetch(`${API_URL}${endpoint}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    if (!res.ok) {
      const text = await res.text();
      throw new Error(text || `Errore ${res.status}`);
    }
    return res.json();
  }, [getToken]);

  const postWithAuth = useCallback(async (endpoint, data, method = 'POST') => {
    const token = await getToken();
    if (!token) throw new Error('Token non disponibile');
    const res = await fetch(`${API_URL}${endpoint}`, {
      method,
      headers: { 
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    });
    if (!res.ok) {
      const text = await res.text();
      throw new Error(text || `Errore ${res.status}`);
    }
    return res.json();
  }, [getToken]);

  const loadDashboard = useCallback(async () => {
    try {
      const [stats, recent] = await Promise.all([
        fetchWithAuth('/api/admin/dashboard-stats'),
        fetchWithAuth('/api/admin/recent?limit=5')
      ]);
      setDashboardStats(stats);
      setRecentItems(recent);
      setLoadedSections(prev => ({ ...prev, dashboard: true }));
    } catch (err) {
      console.error('Dashboard load error:', err);
    }
  }, [fetchWithAuth]);

  // Carica i totali spesi per ogni cliente (chiamata a batch per evitare timeout)
  const loadCustomerTotals = useCallback(async () => {
    if (stripeCustomers.length === 0) return;
    
    setLoadingTotals(true);
    const BATCH_SIZE = 25; // Numero di clienti per batch
    const customerIds = stripeCustomers.map(c => c.id);
    const totalBatches = Math.ceil(customerIds.length / BATCH_SIZE);
    
    try {
      const token = await getToken();
      if (!token) throw new Error('Token non disponibile');
      
      // Processa i clienti a batch
      for (let i = 0; i < totalBatches; i++) {
        const batchIds = customerIds.slice(i * BATCH_SIZE, (i + 1) * BATCH_SIZE);
        
        try {
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 30000);
          
          const res = await fetch(
            `${API_URL}/api/admin/customers/totals?customer_ids=${batchIds.join(',')}`, 
            {
              headers: { Authorization: `Bearer ${token}` },
              signal: controller.signal
            }
          );
          
          clearTimeout(timeoutId);
          
          if (res.ok) {
            const data = await res.json();
            // Aggiorna i totali progressivamente
            setCustomerTotals(prev => ({
              ...prev,
              ...(data.totals || {})
            }));
          }
        } catch (batchErr) {
          console.error(`Batch ${i + 1}/${totalBatches} error:`, batchErr);
          // Continua con il prossimo batch anche se uno fallisce
        }
      }
    } catch (err) {
      console.error('Customer totals load error:', err);
    } finally {
      setLoadingTotals(false);
    }
  }, [getToken, stripeCustomers]);

  const loadStripeCustomers = useCallback(async () => {
    try {
      // Load both Stripe customers and local Firebase data
      const [customersData, localData] = await Promise.all([
        fetchWithAuth('/api/admin/customers?limit=200'),
        fetchWithAuth('/api/admin/stripe-customers/local')
      ]);
      setStripeCustomers(customersData.customers || []);
      setStripeCustomersLocal(localData.customers || {});
      setLoadedSections(prev => ({ ...prev, stripeCustomers: true }));
    } catch (err) {
      console.error('Stripe customers load error:', err);
    }
  }, [fetchWithAuth]);

  // Dopo il caricamento dei clienti Stripe, avvia il caricamento dei totali in background
  useEffect(() => {
    if (loadedSections.stripeCustomers && Object.keys(customerTotals).length === 0 && !loadingTotals) {
      loadCustomerTotals();
    }
  }, [loadedSections.stripeCustomers, customerTotals, loadingTotals, loadCustomerTotals]);

  // Sync Stripe customers to Firebase
  const syncStripeCustomersToFirebase = async () => {
    setSyncingStripeCustomers(true);
    try {
      const result = await postWithAuth('/api/admin/stripe-customers/sync', {});
      alert(result.message || 'Clienti sincronizzati con successo!');
      // Reload local data
      const localData = await fetchWithAuth('/api/admin/stripe-customers/local');
      setStripeCustomersLocal(localData.customers || {});
    } catch (err) {
      alert('Errore sincronizzazione: ' + err.message);
    } finally {
      setSyncingStripeCustomers(false);
    }
  };

  // Update local Stripe customer data
  const updateStripeCustomerLocal = async (customerId, data) => {
    setSavingStripeCustomer(true);
    try {
      const result = await postWithAuth(`/api/admin/stripe-customers/${customerId}`, data, 'PUT');
      // Update local state
      setStripeCustomersLocal(prev => ({
        ...prev,
        [customerId]: { ...prev[customerId], ...data }
      }));
      setShowStripeCustomerEditModal(false);
      setEditingStripeCustomer(null);
    } catch (err) {
      alert('Errore salvataggio: ' + err.message);
    } finally {
      setSavingStripeCustomer(false);
    }
  };

  // Load Stripe transactions with server-side pagination
  const loadStripeTransactions = useCallback(async (reset = true) => {
    setTransactionsPagination(prev => ({ ...prev, loading: true }));
    try {
      const cursor = reset ? '' : (transactionsPagination.nextCursor || '');
      const url = `/api/admin/stripe-transactions?limit=50${cursor ? `&starting_after=${cursor}` : ''}`;
      const data = await fetchWithAuth(url);
      
      if (reset) {
        setStripeTransactions(data.transactions || []);
      } else {
        setStripeTransactions(prev => [...prev, ...(data.transactions || [])]);
      }
      
      setTransactionsPagination(prev => ({
        ...prev,
        hasMore: data.pagination?.has_more || false,
        nextCursor: data.pagination?.next_cursor || null,
        loading: false,
        searchMode: false
      }));
      setLoadedSections(prev => ({ ...prev, stripeTransactions: true }));
    } catch (err) {
      console.error('Stripe transactions load error:', err);
      setTransactionsPagination(prev => ({ ...prev, loading: false }));
    }
  }, [fetchWithAuth, transactionsPagination.nextCursor]);

  // Search transactions (server-side)
  const searchStripeTransactions = useCallback(async (searchQuery) => {
    if (!searchQuery || searchQuery.length < 2) {
      // Reset to normal pagination mode
      loadStripeTransactions(true);
      return;
    }
    
    setTransactionsPagination(prev => ({ ...prev, loading: true, searchMode: true }));
    try {
      const data = await fetchWithAuth(`/api/admin/stripe-transactions/search?q=${encodeURIComponent(searchQuery)}&limit=100`);
      setStripeTransactions(data.transactions || []);
      setTransactionsPagination(prev => ({
        ...prev,
        hasMore: false, // Search results don't have pagination
        nextCursor: null,
        loading: false,
        searchMode: true
      }));
    } catch (err) {
      console.error('Search transactions error:', err);
      setTransactionsPagination(prev => ({ ...prev, loading: false }));
    }
  }, [fetchWithAuth]);

  const loadManualCustomers = useCallback(async () => {
    try {
      const data = await fetchWithAuth('/api/admin/manual-customers');
      setManualCustomers(data.customers || []);
      setLoadedSections(prev => ({ ...prev, manualCustomers: true }));
    } catch (err) {
      console.error('Manual customers load error:', err);
    }
  }, [fetchWithAuth]);

  const loadConfigurations = useCallback(async () => {
    try {
      const data = await fetchWithAuth('/api/admin/configurations');
      setConfigurations(data.configurations || []);
      setLoadedSections(prev => ({ ...prev, configurations: true }));
    } catch (err) {
      console.error('Configurations load error:', err);
    }
  }, [fetchWithAuth]);

  const loadProducts = useCallback(async () => {
    try {
      const data = await fetchWithAuth('/api/admin/products');
      setProducts(data.products || []);
      setLoadedSections(prev => ({ ...prev, products: true }));
    } catch (err) {
      console.error('Products load error:', err);
    }
  }, [fetchWithAuth]);

  const loadFirebaseProducts = useCallback(async () => {
    try {
      const data = await fetchWithAuth('/api/admin/firebase-products?active_only=true');
      setFirebaseProducts(data.products || []);
      setLoadedSections(prev => ({ ...prev, firebaseProducts: true }));
    } catch (err) {
      console.error('Firebase products load error:', err);
    }
  }, [fetchWithAuth]);

  const loadStripeTransactionDetail = useCallback(async (invoiceId) => {
    setLoadingTransactionDetail(true);
    try {
      const data = await fetchWithAuth(`/api/admin/stripe-transactions/${invoiceId}`);
      setStripeTransactionDetail(data);
    } catch (err) {
      console.error('Transaction detail load error:', err);
    }
    setLoadingTransactionDetail(false);
  }, [fetchWithAuth]);

  const syncProductsFromStripe = async () => {
    setSyncingProducts(true);
    try {
      const result = await postWithAuth('/api/admin/products/sync', {});
      alert(result.message || 'Prodotti sincronizzati con successo!');
      await loadFirebaseProducts();
      await loadProducts();
    } catch (err) {
      alert('Errore sincronizzazione: ' + err.message);
    } finally {
      setSyncingProducts(false);
    }
  };

  const loadStripeCustomerDetail = useCallback(async (customerId) => {
    setLoadingDetail(true);
    try {
      const data = await fetchWithAuth(`/api/admin/customers/${customerId}`);
      setStripeCustomerDetail(data);
    } catch (err) {
      console.error('Customer detail load error:', err);
    }
    setLoadingDetail(false);
  }, [fetchWithAuth]);

  const loadManualCustomerDetail = useCallback(async (customerId) => {
    setLoadingManualDetail(true);
    try {
      const data = await fetchWithAuth(`/api/admin/manual-customers/${customerId}`);
      setManualCustomerDetail(data);
    } catch (err) {
      console.error('Manual customer detail load error:', err);
      // Fallback: use data from the list if API fails
      const customerFromList = manualCustomers.find(c => c.id === customerId);
      if (customerFromList) {
        setManualCustomerDetail({
          customer: customerFromList,
          transactions: [],
          total_paid: customerFromList.total_paid || 0
        });
      }
    }
    setLoadingManualDetail(false);
  }, [fetchWithAuth, manualCustomers]);

  const loadAllData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      await Promise.all([
        loadDashboard(),
        loadStripeCustomers(),
        loadManualCustomers(),
        loadConfigurations(),
        loadProducts(),
        loadFirebaseProducts()
      ]);
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  }, [loadDashboard, loadStripeCustomers, loadManualCustomers, loadConfigurations, loadProducts, loadFirebaseProducts]);

  // Load with progress percentage - progressive loading
  const loadWithProgress = useCallback(async () => {
    setLoading(true);
    setLoadingProgress(0);
    setError(null);
    
    // Note: Transactions are loaded on-demand with server-side pagination, not at startup
    const steps = [
      { fn: loadDashboard, progress: 20, label: 'Dashboard' },
      { fn: loadFirebaseProducts, progress: 40, label: 'Prodotti' },
      { fn: loadManualCustomers, progress: 60, label: 'Clienti Manuali' },
      { fn: loadConfigurations, progress: 80, label: 'Configurazioni' },
      { fn: loadStripeCustomers, progress: 100, label: 'Clienti Stripe' }
    ];
    
    for (const step of steps) {
      try {
        await step.fn();
        setLoadingProgress(step.progress);
      } catch (err) {
        console.error(`Error loading ${step.label}:`, err);
      }
    }
    
    setLoading(false);
    setInitialLoadComplete(true);
  }, [loadDashboard, loadFirebaseProducts, loadManualCustomers, loadConfigurations, loadStripeCustomers]);

  // Load data based on current tab (for subsequent navigation)
  const loadTabData = useCallback(async (tabId, forceReload = false) => {
    // Skip if data already loaded and not forcing reload
    const sectionMap = {
      'dashboard': ['dashboard', 'manualCustomers', 'configurations'],
      'stripe-customers': ['stripeCustomers'],
      'stripe-transactions': ['stripeTransactions'],
      'manual-customers': ['manualCustomers'],
      'configurations': ['configurations'],
      'products': ['products']
    };
    
    const sections = sectionMap[tabId] || [];
    const needsLoad = forceReload || sections.some(s => !loadedSections[s]);
    
    if (!needsLoad) return;
    
    setLoadingTab(true);
    try {
      switch (tabId) {
        case 'dashboard':
          await Promise.all([
            !loadedSections.manualCustomers && loadManualCustomers(),
            !loadedSections.configurations && loadConfigurations()
          ].filter(Boolean));
          break;
        case 'stripe-customers':
          if (!loadedSections.stripeCustomers || forceReload) await loadStripeCustomers();
          break;
        case 'stripe-transactions':
          // Always load first page of transactions when visiting tab
          if (!loadedSections.stripeTransactions || forceReload) {
            await loadStripeTransactions(true);
          }
          break;
        case 'manual-customers':
          if (!loadedSections.manualCustomers || forceReload) await loadManualCustomers();
          break;
        case 'configurations':
          if (!loadedSections.configurations || forceReload) await loadConfigurations();
          break;
        case 'products':
          if (!loadedSections.products || forceReload) await loadProducts();
          break;
        default:
          break;
      }
    } catch (err) {
      console.error('Tab data load error:', err);
    } finally {
      setLoadingTab(false);
    }
  }, [loadStripeCustomers, loadManualCustomers, loadConfigurations, loadProducts, loadedSections]);

  useEffect(() => {
    if (!user || !isAdmin) {
      navigate('/admin/login');
      return;
    }
    // Load all data with progress on initial mount
    loadWithProgress();
  }, [user, isAdmin, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load tab data when tab changes (only if initial load complete)
  useEffect(() => {
    if (initialLoadComplete && user && isAdmin) {
      loadTabData(activeTab);
    }
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load detail when customer selected
  useEffect(() => {
    if (selectedStripeCustomer) {
      (async () => {
        await loadStripeCustomerDetail(selectedStripeCustomer);
      })();
    }
  }, [selectedStripeCustomer]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load detail when transaction selected
  useEffect(() => {
    if (selectedStripeTransaction) {
      (async () => {
        await loadStripeTransactionDetail(selectedStripeTransaction);
      })();
    }
  }, [selectedStripeTransaction]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load manual customer detail when selected
  useEffect(() => {
    if (selectedManualCustomer) {
      (async () => {
        await loadManualCustomerDetail(selectedManualCustomer);
      })();
    }
  }, [selectedManualCustomer]); // eslint-disable-line react-hooks/exhaustive-deps

  // ==================== CRUD OPERATIONS ====================

  const saveManualCustomer = async () => {
    setSavingCustomer(true);
    try {
      if (editMode && selectedItem) {
        await postWithAuth(`/api/admin/manual-customers/${selectedItem.id}`, formData, 'PUT');
      } else {
        await postWithAuth('/api/admin/manual-customers', formData);
      }
      setShowCustomerModal(false);
      setFormData({});
      setEditMode(false);
      await loadManualCustomers();
      await loadDashboard();
    } catch (err) {
      alert('Errore: ' + err.message);
    } finally {
      setSavingCustomer(false);
    }
  };

  const deleteManualCustomer = async (id) => {
    if (!window.confirm('Eliminare questo cliente e tutte le sue transazioni?')) return;
    setDeletingCustomer(true);
    try {
      await postWithAuth(`/api/admin/manual-customers/${id}`, {}, 'DELETE');
      // Close the detail panel if it's open for this customer
      if (selectedManualCustomer === id) {
        setSelectedManualCustomer(null);
        setManualCustomerDetail(null);
      }
      await loadManualCustomers();
      await loadDashboard();
    } catch (err) {
      alert('Errore: ' + err.message);
    } finally {
      setDeletingCustomer(false);
    }
  };

  const saveTransaction = async () => {
    setSavingTransaction(true);
    try {
      await postWithAuth('/api/admin/manual-transactions', formData);
      setShowTransactionModal(false);
      setFormData({});
      // Reload customer detail if panel is open
      if (selectedManualCustomer) {
        await loadManualCustomerDetail(selectedManualCustomer);
      }
      await loadManualCustomers();
      await loadDashboard();
    } catch (err) {
      alert('Errore: ' + err.message);
    } finally {
      setSavingTransaction(false);
    }
  };

  const deleteTransaction = async (transactionId) => {
    if (!window.confirm('Eliminare questa transazione?')) return;
    setDeletingTransaction(transactionId);
    try {
      await postWithAuth(`/api/admin/manual-transactions/${transactionId}`, {}, 'DELETE');
      // Reload customer detail if panel is open
      if (selectedManualCustomer) {
        await loadManualCustomerDetail(selectedManualCustomer);
      }
      await loadManualCustomers();
      await loadDashboard();
    } catch (err) {
      alert('Errore: ' + err.message);
    } finally {
      setDeletingTransaction(null);
    }
  };

  // State for inline editing in sidebar
  const [editingCustomerInline, setEditingCustomerInline] = useState(false);
  const [inlineFormData, setInlineFormData] = useState({});

  const startInlineEdit = (customer) => {
    setInlineFormData({ ...customer });
    setEditingCustomerInline(true);
  };

  const cancelInlineEdit = () => {
    setEditingCustomerInline(false);
    setInlineFormData({});
  };

  const saveInlineEdit = async () => {
    setSavingInlineEdit(true);
    try {
      await postWithAuth(`/api/admin/manual-customers/${selectedManualCustomer}`, inlineFormData, 'PUT');
      setEditingCustomerInline(false);
      setInlineFormData({});
      await loadManualCustomerDetail(selectedManualCustomer);
      await loadManualCustomers();
    } catch (err) {
      alert('Errore: ' + err.message);
    } finally {
      setSavingInlineEdit(false);
    }
  };

  const saveConfiguration = async () => {
    setSavingConfig(true);
    try {
      if (editMode && selectedItem) {
        await postWithAuth(`/api/admin/configurations/${selectedItem.id}`, formData, 'PUT');
      } else {
        await postWithAuth('/api/admin/configurations', formData);
      }
      setShowConfigModal(false);
      setFormData({});
      setEditMode(false);
      await loadConfigurations();
      await loadDashboard();
    } catch (err) {
      alert('Errore: ' + err.message);
    } finally {
      setSavingConfig(false);
    }
  };

  const updateConfigStatus = async (id, status) => {
    try {
      await postWithAuth(`/api/admin/configurations/${id}`, { status }, 'PUT');
      await loadConfigurations();
      await loadDashboard();
    } catch (err) {
      alert('Errore: ' + err.message);
    }
  };

  const deleteConfiguration = async (id) => {
    if (!window.confirm('Eliminare questa configurazione?')) return;
    try {
      await postWithAuth(`/api/admin/configurations/${id}`, {}, 'DELETE');
      await loadConfigurations();
      await loadDashboard();
    } catch (err) {
      alert('Errore: ' + err.message);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  // ==================== FILTERING & SORTING ====================

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const filterAndSort = (items, searchFields, statusField = 'status') => {
    let result = items.filter(item => {
      const matchesSearch = !searchTerm || searchFields.some(field => 
        item[field]?.toLowerCase?.().includes(searchTerm.toLowerCase())
      );
      const itemStatus = item[statusField] || item.status || item.subscription_status;
      const matchesStatus = !statusFilter || statusFilter === 'all' || itemStatus === statusFilter;
      return matchesSearch && matchesStatus;
    });
    
    result.sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];
      
      // Handle date sorting
      if (sortField === 'next_renewal' || sortField === 'created' || sortField === 'created_at') {
        aVal = aVal ? new Date(aVal).getTime() : 0;
        bVal = bVal ? new Date(bVal).getTime() : 0;
      }
      
      // Handle null values - put them at the end
      if (aVal === null || aVal === undefined) return sortDirection === 'asc' ? 1 : -1;
      if (bVal === null || bVal === undefined) return sortDirection === 'asc' ? -1 : 1;
      
      if (typeof aVal === 'number') return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
      return sortDirection === 'asc' ? String(aVal).localeCompare(String(bVal)) : String(bVal).localeCompare(String(aVal));
    });
    
    return result;
  };

  // Get unique statuses from Stripe customers for dynamic filter
  const getStripeStatuses = () => {
    const statuses = new Set();
    stripeCustomers.forEach(c => {
      if (c.subscription_status) statuses.add(c.subscription_status);
    });
    return Array.from(statuses);
  };

  // Get unique statuses from Manual customers for dynamic filter
  const getManualStatuses = () => {
    const statuses = new Set();
    manualCustomers.forEach(c => {
      if (c.status) statuses.add(c.status);
    });
    return Array.from(statuses);
  };

  // Helper to calculate next renewal date from a given date
  const calculateNextRenewalFromDate = (fromDate, planType) => {
    if (!fromDate || planType === 'one_time') return null;
    
    const date = new Date(fromDate);
    if (planType === 'monthly') {
      date.setMonth(date.getMonth() + 1);
    } else if (planType === 'yearly') {
      date.setFullYear(date.getFullYear() + 1);
    }
    return date.toISOString().split('T')[0];
  };

  // Get next renewal - prefers stored value, falls back to calculation
  const getNextRenewal = (customer, transactions = []) => {
    if (!customer || customer.plan_type === 'one_time') return null;
    
    // If customer has a stored next_renewal, use it
    if (customer.next_renewal) {
      return customer.next_renewal;
    }
    
    // Fallback: calculate from last transaction or start_date
    let lastPaymentDate = customer.start_date ? new Date(customer.start_date) : null;
    
    if (transactions && transactions.length > 0) {
      const sortedTransactions = [...transactions].sort((a, b) => {
        const dateA = a.date ? new Date(a.date).getTime() : 0;
        const dateB = b.date ? new Date(b.date).getTime() : 0;
        return dateB - dateA;
      });
      const lastTransaction = sortedTransactions[0];
      if (lastTransaction?.date) {
        lastPaymentDate = new Date(lastTransaction.date);
      }
    }
    
    if (!lastPaymentDate) return null;
    
    return calculateNextRenewalFromDate(lastPaymentDate, customer.plan_type);
  };

  // Check if customer payment is overdue
  const isPaymentOverdue = (customer, transactions = []) => {
    if (!customer || customer.plan_type === 'one_time' || customer.status === 'canceled') return false;
    
    const nextRenewal = getNextRenewal(customer, transactions);
    if (!nextRenewal) return false;
    
    return new Date(nextRenewal) < new Date();
  };

  // Enrich manual customers with next_renewal (use stored value or calculate)
  const enrichedManualCustomers = manualCustomers.map(c => {
    return {
      ...c,
      next_renewal: c.next_renewal || getNextRenewal(c, [])
    };
  });

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    // Handle both ISO dates and simple date strings (YYYY-MM-DD)
    let date;
    if (dateStr.includes('T')) {
      date = new Date(dateStr);
    } else {
      // For simple date strings, parse them without timezone conversion
      const [year, month, day] = dateStr.split('-').map(Number);
      date = new Date(year, month - 1, day);
    }
    return date.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  // Days until date
  const daysUntil = (dateStr) => {
    if (!dateStr) return null;
    let date;
    if (dateStr.includes('T')) {
      date = new Date(dateStr);
    } else {
      const [year, month, day] = dateStr.split('-').map(Number);
      date = new Date(year, month - 1, day);
    }
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    date.setHours(0, 0, 0, 0);
    const diff = Math.ceil((date - now) / (1000 * 60 * 60 * 24));
    return diff;
  };

  // ==================== RENDER SECTIONS ====================

  // Dashboard Tab
  const renderDashboard = () => {
    // Plan type labels
    const planLabels = {
      monthly: 'Mensile',
      yearly: 'Annuale',
      one_time: 'Una tantum'
    };
    
    // Status labels
    const statusLabels = {
      active: 'Attivo',
      suspended: 'Sospeso',
      canceled: 'Cancellato',
      pending: 'Da fare',
      completed: 'Completata'
    };
    
    return (
      <div className="space-y-6">
        {/* Top Stats Cards - No revenue */}
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
          <StatCard icon={CreditCard} label="Clienti Stripe" value={dashboardStats?.stripe_customers || 0} color="bg-blue-500" subtext={`${dashboardStats?.stripe_active_subscriptions || 0} attivi`} onClick={() => setActiveTab('stripe-customers')} />
          <StatCard icon={Users} label="Clienti Manuali" value={dashboardStats?.manual_customers_total || 0} color="bg-purple-500" subtext={`${dashboardStats?.manual_customers_active || 0} attivi`} onClick={() => setActiveTab('manual-customers')} />
          <StatCard icon={Settings} label="Configurazioni" value={dashboardStats?.configurations_total || 0} color="bg-amber-500" subtext={`${dashboardStats?.configurations_pending || 0} da fare`} onClick={() => setActiveTab('configurations')} />
        </div>

        {/* Summary Cards - No revenue */}
        <div className="grid md:grid-cols-3 gap-4">
          <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4">
            <h4 className="text-white font-semibold mb-3 flex items-center gap-2"><CreditCard className="w-4 h-4 text-blue-400" /> Stripe</h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-slate-400">Attivi</span><span className="text-emerald-400">{dashboardStats?.stripe_active_subscriptions || 0}</span></div>
              <div className="flex justify-between"><span className="text-slate-400">Trial</span><span className="text-blue-400">{dashboardStats?.stripe_trialing_subscriptions || 0}</span></div>
              <div className="flex justify-between"><span className="text-slate-400">Scaduto</span><span className="text-amber-400">{dashboardStats?.stripe_past_due_subscriptions || 0}</span></div>
              <div className="flex justify-between"><span className="text-slate-400">Cancellati</span><span className="text-red-400">{dashboardStats?.stripe_canceled_subscriptions || 0}</span></div>
              <div className="flex justify-between"><span className="text-slate-400">Incompleto</span><span className="text-orange-400">{dashboardStats?.stripe_incomplete_subscriptions || 0}</span></div>
              <div className="flex justify-between"><span className="text-slate-400">Scaduto Incompleto</span><span className="text-red-300">{dashboardStats?.stripe_incomplete_expired_subscriptions || 0}</span></div>
              <div className="flex justify-between"><span className="text-slate-400">Non Pagato</span><span className="text-red-500">{dashboardStats?.stripe_unpaid_subscriptions || 0}</span></div>
              <div className="flex justify-between"><span className="text-slate-400">In Pausa</span><span className="text-slate-300">{dashboardStats?.stripe_paused_subscriptions || 0}</span></div>
              <div className="flex justify-between"><span className="text-slate-400">Nessuno</span><span className="text-slate-500">{dashboardStats?.stripe_no_subscription || 0}</span></div>
            </div>
          </div>
          <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4">
            <h4 className="text-white font-semibold mb-3 flex items-center gap-2"><Users className="w-4 h-4 text-purple-400" /> Manuali</h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-slate-400">Attivi</span><span className="text-emerald-400">{dashboardStats?.manual_customers_active || 0}</span></div>
              <div className="flex justify-between"><span className="text-slate-400">Sospesi</span><span className="text-amber-400">{dashboardStats?.manual_customers_suspended || 0}</span></div>
              <div className="flex justify-between"><span className="text-slate-400">Cancellati</span><span className="text-red-400">{dashboardStats?.manual_customers_canceled || 0}</span></div>
            </div>
          </div>
          <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4">
            <h4 className="text-white font-semibold mb-3 flex items-center gap-2"><Settings className="w-4 h-4 text-amber-400" /> Configurazioni</h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-slate-400">Da fare</span><span className="text-amber-400">{dashboardStats?.configurations_pending || 0}</span></div>
              <div className="flex justify-between"><span className="text-slate-400">Completate</span><span className="text-emerald-400">{dashboardStats?.configurations_completed || 0}</span></div>
              <div className="flex justify-between"><span className="text-slate-400">Cancellate</span><span className="text-red-400">{dashboardStats?.configurations_canceled || 0}</span></div>
            </div>
          </div>
        </div>

        {/* Recent Items Grid - 3 columns */}
        <div className="grid md:grid-cols-3 gap-6">
          {/* Ultimi Clienti Stripe */}
          <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4">
            <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
              <CreditCard className="w-4 h-4 text-blue-400" /> Ultimi Clienti Stripe
            </h4>
            <div className="space-y-3">
              {recentItems?.recent_stripe_customers?.slice(0, 5).map(c => {
                const daysUntilRenewal = c.next_renewal ? Math.ceil((new Date(c.next_renewal) - new Date()) / (1000 * 60 * 60 * 24)) : null;
                return (
                  <div 
                    key={c.id} 
                    className="p-3 bg-slate-900/50 rounded-lg border border-slate-700/50 hover:border-blue-500/30 cursor-pointer transition-colors"
                    onClick={() => { setActiveTab('stripe-customers'); setSelectedStripeCustomer(c.id); loadStripeCustomerDetail(c.id); }}
                  >
                    {/* Header: Nome + Stato */}
                    <div className="flex items-start justify-between mb-2">
                      <p className="text-white text-sm font-medium">{c.name || 'N/A'}</p>
                      <span className={`px-2 py-0.5 rounded text-xs ${c.subscription_status === 'active' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-500/20 text-slate-400'}`}>
                        {statusLabels[c.subscription_status] || c.subscription_status || 'N/A'}
                      </span>
                    </div>
                    
                    {/* Email */}
                    <p className="text-slate-400 text-xs mb-2">{c.email}</p>
                    
                    {/* Piano */}
                    {c.plan_name && (
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-blue-400 text-xs">{c.plan_name}</span>
                        {c.plan_amount > 0 && (
                          <span className="text-slate-300 text-xs">
                            €{c.plan_amount}/{c.plan_interval === 'month' ? 'mese' : 'anno'}
                          </span>
                        )}
                      </div>
                    )}
                    
                    {/* Prossimo Rinnovo */}
                    {c.next_renewal && (
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-slate-500 text-xs">Prossimo Rinnovo</span>
                        <span className="text-slate-300 text-xs">
                          {formatDate(c.next_renewal)}
                          {daysUntilRenewal !== null && (
                            <span className={`ml-1 ${daysUntilRenewal < 30 ? 'text-amber-400' : 'text-slate-500'}`}>
                              ({daysUntilRenewal > 0 ? `tra ${daysUntilRenewal}g` : 'scaduto'})
                            </span>
                          )}
                        </span>
                      </div>
                    )}
                    
                    {/* Totale Speso */}
                    {c.total_spent > 0 && (
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-slate-500 text-xs">Totale Speso</span>
                        <span className="text-emerald-400 text-xs font-medium">
                          €{c.total_spent?.toFixed(0)}
                          {c.invoice_count > 0 && <span className="text-slate-500 ml-1">({c.invoice_count} fatt.)</span>}
                        </span>
                      </div>
                    )}
                    
                    {/* Data Registrazione */}
                    {c.created && (
                      <div className="flex items-center justify-between">
                        <span className="text-slate-500 text-xs">Registrato</span>
                        <span className="text-slate-400 text-xs">{formatDate(c.created)}</span>
                      </div>
                    )}
                  </div>
                );
              })}
              {(!recentItems?.recent_stripe_customers || recentItems.recent_stripe_customers.length === 0) && (
                <p className="text-slate-500 text-sm text-center py-4">Nessun cliente</p>
              )}
            </div>
          </div>

          {/* Ultimi Clienti Manuali */}
          <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4">
            <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
              <Users className="w-4 h-4 text-purple-400" /> Ultimi Clienti Manuali
            </h4>
            <div className="space-y-3">
              {manualCustomers?.slice(0, 5).map(c => (
                <div 
                  key={c.id} 
                  className="p-3 bg-slate-900/50 rounded-lg border border-slate-700/50 hover:border-purple-500/30 cursor-pointer transition-colors"
                  onClick={() => { setActiveTab('manual-customers'); setSelectedManualCustomer(c.id); loadManualCustomerDetail(c.id); }}
                >
                  <div className="flex items-start justify-between mb-2">
                    <p className="text-white text-sm font-medium">{c.name || 'N/A'}</p>
                    <span className={`px-2 py-0.5 rounded text-xs ${c.status === 'active' ? 'bg-emerald-500/20 text-emerald-400' : c.status === 'suspended' ? 'bg-amber-500/20 text-amber-400' : 'bg-red-500/20 text-red-400'}`}>
                      {statusLabels[c.status] || c.status}
                    </span>
                  </div>
                  <p className="text-slate-400 text-xs mb-1">{c.email}</p>
                  {c.product && (
                    <p className="text-purple-400 text-xs">
                      {c.product} - {planLabels[c.plan_type] || c.plan_type}
                      {c.price_amount > 0 && <span className="text-emerald-400 ml-1">€{c.price_amount}</span>}
                    </p>
                  )}
                  {c.next_renewal && c.plan_type !== 'one_time' && (
                    <p className="text-slate-500 text-xs mt-1">Rinnovo: {formatDate(c.next_renewal)}</p>
                  )}
                </div>
              ))}
              {(!manualCustomers || manualCustomers.length === 0) && (
                <p className="text-slate-500 text-sm text-center py-4">Nessun cliente</p>
              )}
            </div>
          </div>

          {/* Ultime Configurazioni */}
          <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4">
            <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
              <Settings className="w-4 h-4 text-amber-400" /> Ultime Configurazioni
            </h4>
            <div className="space-y-3">
              {configurations?.slice(0, 5).map(c => (
                <div 
                  key={c.id} 
                  className="p-3 bg-slate-900/50 rounded-lg border border-slate-700/50 hover:border-amber-500/30 cursor-pointer transition-colors"
                  onClick={() => setActiveTab('configurations')}
                >
                  <div className="flex items-start justify-between mb-2">
                    <p className="text-white text-sm font-medium">{c.business_name || c.name || 'N/A'}</p>
                    <span className={`px-2 py-0.5 rounded text-xs ${c.status === 'pending' ? 'bg-amber-500/20 text-amber-400' : c.status === 'completed' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}`}>
                      {statusLabels[c.status] || c.status || 'Da fare'}
                    </span>
                  </div>
                  <p className="text-slate-400 text-xs mb-1">{c.email || c.customer_email}</p>
                  {c.phone && <p className="text-slate-500 text-xs">{c.phone}</p>}
                  {c.business_type && <p className="text-amber-400 text-xs mt-1">{c.business_type}</p>}
                  {c.created_at && (
                    <p className="text-slate-500 text-xs mt-1">Creata: {formatDate(c.created_at)}</p>
                  )}
                </div>
              ))}
              {(!configurations || configurations.length === 0) && (
                <p className="text-slate-500 text-sm text-center py-4">Nessuna configurazione</p>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Stripe Customers Tab - ORIGINAL LAYOUT with right sidebar
  const renderStripeCustomers = () => {
    // Apply filters - include company_name from local data in search
    let customersToFilter = stripeCustomers.map(c => ({
      ...c,
      company_name: stripeCustomersLocal[c.id]?.company_name || '',
      total_spent: customerTotals[c.id]?.total_spent || 0,
      invoices_count: customerTotals[c.id]?.invoices_count || 0
    }));
    
    // Apply product filter (supports variant format: "ProductName|interval|price")
    if (productFilter) {
      if (productFilter.includes('|')) {
        const [productName, interval, priceStr] = productFilter.split('|');
        const price = parseFloat(priceStr);
        customersToFilter = customersToFilter.filter(c => 
          c.plan_name === productName && c.plan_interval === interval && c.plan_amount === price
        );
      } else {
        customersToFilter = customersToFilter.filter(c => c.plan_name === productFilter);
      }
    }
    
    // Apply plan type filter (interval)
    if (planTypeFilter) {
      const interval = planTypeFilter === 'monthly' ? 'month' : planTypeFilter === 'yearly' ? 'year' : 'one_time';
      customersToFilter = customersToFilter.filter(c => c.plan_interval === interval);
    }
    
    // Include company_name in search
    const filtered = filterAndSort(customersToFilter, ['email', 'name', 'plan_name', 'company_name'], 'subscription_status');
    const availableStatuses = getStripeStatuses();
    
    // Get unique product variants for filter
    const productVariants = new Map();
    stripeCustomers.forEach(c => {
      if (c.plan_name && c.plan_interval && c.plan_amount) {
        const key = `${c.plan_name}|${c.plan_interval}|${c.plan_amount}`;
        if (!productVariants.has(key)) {
          productVariants.set(key, { name: c.plan_name, interval: c.plan_interval, amount: c.plan_amount, key: key });
        }
      }
    });
    const availableVariants = Array.from(productVariants.values()).sort((a, b) => {
      const nameCompare = a.name.localeCompare(b.name);
      if (nameCompare !== 0) return nameCompare;
      return a.interval === 'month' ? -1 : 1;
    });
    
    // Status labels in Italian
    const statusLabels = {
      active: 'Attivo',
      canceled: 'Cancellato',
      past_due: 'Scaduto',
      trialing: 'Trial',
      incomplete: 'Incompleto',
      incomplete_expired: 'Scaduto Incompleto',
      unpaid: 'Non Pagato',
      paused: 'In Pausa',
      none: 'Nessuno'
    };
    
    // Get local data for selected customer
    const selectedLocalData = selectedStripeCustomer ? stripeCustomersLocal[selectedStripeCustomer] : null;
    
    return (
      <div className="flex gap-6">
        {/* Main Table */}
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap gap-3 items-center justify-between mb-4">
            <div>
              <h2 className="text-xl font-bold text-white mb-1">Clienti Stripe</h2>
              <p className="text-slate-400 text-sm">Tutti i clienti registrati su Stripe</p>
            </div>
            <Button 
              onClick={syncStripeCustomersToFirebase} 
              disabled={syncingStripeCustomers}
              variant="outline"
              className="border-slate-600 text-slate-300 hover:text-white"
            >
              {syncingStripeCustomers ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
              Sincronizza
            </Button>
          </div>
          
          <div className="flex flex-wrap gap-3 items-center mb-4">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Cerca per email, nome, azienda..." className="w-full bg-slate-800/50 border border-slate-700 rounded-lg pl-10 pr-4 py-2.5 text-white text-sm focus:border-emerald-500 focus:outline-none" />
            </div>
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="bg-slate-800/50 border border-slate-700 rounded-lg px-4 py-2.5 text-white text-sm">
              <option value="">Tutti gli stati</option>
              {availableStatuses.map(status => (
                <option key={status} value={status}>{statusLabels[status] || status}</option>
              ))}
            </select>
            <select value={planTypeFilter} onChange={e => setPlanTypeFilter(e.target.value)} className="bg-slate-800/50 border border-slate-700 rounded-lg px-4 py-2.5 text-white text-sm">
              <option value="">Tutti i piani</option>
              <option value="monthly">Mensili</option>
              <option value="yearly">Annuali</option>
            </select>
            <select value={productFilter} onChange={e => setProductFilter(e.target.value)} className="bg-slate-800/50 border border-slate-700 rounded-lg px-4 py-2.5 text-white text-sm min-w-[220px]">
              <option value="">Tutti i prodotti</option>
              {availableVariants.map(variant => {
                const intervalLabel = variant.interval === 'month' ? 'Mensile' : 'Annuale';
                return (
                  <option key={variant.key} value={variant.key}>
                    {variant.name} - {intervalLabel} (€{variant.amount})
                  </option>
                );
              })}
            </select>
          </div>

          <div className="bg-slate-800/30 border border-slate-700 rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-700">
                    <th className="text-left p-4 text-xs text-slate-400 font-medium">Cliente</th>
                    <th className="text-left p-4 text-xs text-slate-400 font-medium">Nome Azienda</th>
                    <th className="text-left p-4 text-xs text-slate-400 font-medium cursor-pointer" onClick={() => handleSort('subscription_status')}>
                      <span className="flex items-center">Stato <SortIcon field="subscription_status" sortField={sortField} sortDirection={sortDirection} /></span>
                    </th>
                    <th className="text-left p-4 text-xs text-slate-400 font-medium">Piano</th>
                    <th className="text-left p-4 text-xs text-slate-400 font-medium cursor-pointer" onClick={() => handleSort('next_renewal')}>
                      <span className="flex items-center">Prossimo Rinnovo <SortIcon field="next_renewal" sortField={sortField} sortDirection={sortDirection} /></span>
                    </th>
                    <th className="text-left p-4 text-xs text-slate-400 font-medium cursor-pointer" onClick={() => handleSort('total_spent')}>
                      <span className="flex items-center">Totale Speso <SortIcon field="total_spent" sortField={sortField} sortDirection={sortDirection} /></span>
                    </th>
                    <th className="text-left p-4 text-xs text-slate-400 font-medium cursor-pointer" onClick={() => handleSort('created')}>
                      <span className="flex items-center">Registrato <SortIcon field="created" sortField={sortField} sortDirection={sortDirection} /></span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(customer => {
                    const days = daysUntil(customer.next_renewal);
                    return (
                      <tr 
                        key={customer.id} 
                        className={`border-b border-slate-700/50 hover:bg-slate-800/50 cursor-pointer transition-colors ${selectedStripeCustomer === customer.id ? 'bg-slate-800/70' : ''}`}
                        onClick={() => setSelectedStripeCustomer(customer.id)}
                      >
                        <td className="p-4">
                          <div className="flex items-center gap-3">
                            <div className="text-slate-400"><ChevronRight className="w-4 h-4" /></div>
                            <div>
                              <p className="text-white text-sm font-medium">{customer.email}</p>
                              <p className="text-slate-400 text-xs">{customer.name || 'N/A'}</p>
                            </div>
                          </div>
                        </td>
                        <td className="p-4">
                          <p className="text-emerald-400 text-sm font-medium">{customer.company_name || <span className="text-slate-500 italic">-</span>}</p>
                        </td>
                        <td className="p-4"><StatusBadge status={customer.subscription_status} /></td>
                        <td className="p-4">
                          <div>
                            <p className="text-white text-sm">{customer.plan_name || 'N/A'}</p>
                            {customer.plan_amount > 0 && <p className="text-slate-400 text-xs">€{customer.plan_amount}/{customer.plan_interval === 'month' ? 'mese' : 'anno'}</p>}
                          </div>
                        </td>
                        <td className="p-4">
                          {customer.next_renewal ? (
                            <div>
                              <p className="text-white text-sm">{formatDate(customer.next_renewal)}</p>
                              {days !== null && days >= 0 && <p className="text-slate-400 text-xs">tra {days} giorni</p>}
                              {days !== null && days < 0 && <p className="text-red-400 text-xs">{Math.abs(days)} giorni fa</p>}
                            </div>
                          ) : <span className="text-slate-500">-</span>}
                        </td>
                        <td className="p-4">
                          <div>
                            {customerTotals[customer.id] !== undefined ? (
                              customer.total_spent > 0 ? (
                                <>
                                  <p className="text-emerald-400 font-medium">€{customer.total_spent}</p>
                                  {customer.invoices_count > 0 && (
                                    <p className="text-slate-400 text-xs">{customer.invoices_count} fatture</p>
                                  )}
                                </>
                              ) : (
                                <p className="text-slate-500 font-medium">€0</p>
                              )
                            ) : loadingTotals ? (
                              <RefreshCw className="w-4 h-4 text-emerald-400 animate-spin" />
                            ) : (
                              <span className="text-slate-500 text-xs italic">-</span>
                            )}
                          </div>
                        </td>
                        <td className="p-4 text-slate-300 text-sm">{formatDate(customer.created)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {filtered.length === 0 && <div className="p-8 text-center text-slate-500">Nessun cliente trovato</div>}
          </div>
        </div>

        {/* Right Sidebar - Customer Detail */}
        {selectedStripeCustomer && (
          <div className="w-80 flex-shrink-0">
            <div className="bg-slate-800/50 border border-slate-700 rounded-xl sticky top-24">
              <div className="flex items-center justify-between p-4 border-b border-slate-700">
                <h3 className="text-white font-semibold">Dettagli Cliente</h3>
                <div className="flex items-center gap-2">
                  <button 
                    onClick={() => { 
                      setEditingStripeCustomer(selectedStripeCustomer);
                      setStripeCustomerFormData({ 
                        company_name: selectedLocalData?.company_name || '' 
                      });
                      setShowStripeCustomerEditModal(true);
                    }} 
                    className="p-1 hover:bg-slate-700 rounded text-slate-400 hover:text-emerald-400"
                    title="Modifica dati locali"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button onClick={() => { setSelectedStripeCustomer(null); setStripeCustomerDetail(null); }} className="p-1 hover:bg-slate-700 rounded text-slate-400 hover:text-white">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
              
              {loadingDetail ? (
                <div className="p-8 text-center">
                  <RefreshCw className="w-6 h-6 text-emerald-500 animate-spin mx-auto" />
                </div>
              ) : stripeCustomerDetail ? (
                <div className="p-4 space-y-4 max-h-[calc(100vh-200px)] overflow-y-auto">
                  {/* Company Name (Local Data) */}
                  {selectedLocalData?.company_name && (
                    <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-3">
                      <p className="text-emerald-400 text-xs mb-1">Nome Azienda</p>
                      <p className="text-white font-semibold">{selectedLocalData.company_name}</p>
                    </div>
                  )}
                  
                  {/* Customer Info */}
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-slate-300">
                      <Mail className="w-4 h-4 text-slate-500" />
                      <span className="text-sm">{stripeCustomerDetail.customer?.email}</span>
                    </div>
                    <div className="flex items-center gap-2 text-slate-300">
                      <User className="w-4 h-4 text-slate-500" />
                      <span className="text-sm">{stripeCustomerDetail.customer?.name || 'N/A'}</span>
                    </div>
                    <div className="flex items-center gap-2 text-slate-300">
                      <Calendar className="w-4 h-4 text-slate-500" />
                      <span className="text-sm">Cliente dal {formatDate(stripeCustomerDetail.customer?.created)}</span>
                    </div>
                  </div>

                  {/* Total Spent */}
                  <div className="pt-2 border-t border-slate-700">
                    <p className="text-slate-400 text-xs mb-1">Totale Speso</p>
                    <p className="text-2xl font-bold text-emerald-400">€{stripeCustomerDetail.total_spent || 0}</p>
                  </div>

                  {/* Payment Methods */}
                  {stripeCustomerDetail.payment_methods?.length > 0 && (
                    <div className="pt-2 border-t border-slate-700">
                      <p className="text-slate-400 text-xs mb-2">Metodo di Pagamento</p>
                      <div className="space-y-1.5">
                        {stripeCustomerDetail.payment_methods.map((pm, i) => (
                          <div key={i} className="flex items-center gap-2 text-slate-300 text-sm">
                            <CreditCard className="w-4 h-4 text-slate-500" />
                            <span>{pm.brand} •••• {pm.last4}</span>
                            <span className="text-slate-500">{pm.exp_month}/{pm.exp_year}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Subscriptions */}
                  {stripeCustomerDetail.subscriptions?.length > 0 && (
                    <div className="pt-2 border-t border-slate-700">
                      <p className="text-slate-400 text-xs mb-2">Abbonamenti</p>
                      <div className="space-y-2">
                        {stripeCustomerDetail.subscriptions.map((sub, i) => (
                          <div key={i} className="bg-slate-900/50 rounded-lg p-3">
                            <div className="flex items-start justify-between mb-1">
                              <p className="text-white text-sm font-medium">{sub.product_name}</p>
                              <StatusBadge status={sub.status} />
                            </div>
                            <p className="text-slate-400 text-xs">€{sub.amount}/{sub.interval === 'month' ? 'mese' : 'anno'}</p>
                            {sub.current_period_end && <p className="text-slate-500 text-xs mt-1">Prossimo rinnovo {formatDate(sub.current_period_end)}</p>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Invoices */}
                  {stripeCustomerDetail.invoices?.length > 0 && (
                    <div className="pt-2 border-t border-slate-700">
                      <p className="text-slate-400 text-xs mb-2">Ultime Fatture ({stripeCustomerDetail.invoices.length})</p>
                      <div className="space-y-1.5">
                        {stripeCustomerDetail.invoices.slice(0, 5).map((inv, i) => (
                          <div key={i} className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <span className="text-white text-sm">€{inv.amount}</span>
                              <span className="text-slate-500 text-xs">{formatDate(inv.created)}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="px-2 py-0.5 rounded text-xs bg-emerald-500/20 text-emerald-400">Pagata</span>
                              {inv.invoice_pdf && (
                                <a href={inv.invoice_pdf} target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-white">
                                  <ExternalLink className="w-3.5 h-3.5" />
                                </a>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Stripe Dashboard Link */}
                  <div className="pt-2 border-t border-slate-700">
                    <a 
                      href={`https://dashboard.stripe.com/customers/${selectedStripeCustomer}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-2 w-full py-2 text-sm text-blue-400 hover:text-blue-300 transition-colors"
                    >
                      <ExternalLink className="w-4 h-4" />
                      Apri su Stripe Dashboard
                    </a>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Manual Customers Tab - Same layout as Stripe with right sidebar
  const renderManualCustomers = () => {
    // First apply product filter, then plan type filter, then other filters
    let customersToFilter = enrichedManualCustomers;
    
    // Apply product filter (supports variant format: "ProductName|interval|price")
    if (productFilter) {
      if (productFilter.includes('|')) {
        const [productName, interval, priceStr] = productFilter.split('|');
        const price = parseFloat(priceStr);
        const planType = interval === 'month' ? 'monthly' : interval === 'year' ? 'yearly' : 'one_time';
        customersToFilter = customersToFilter.filter(c => 
          c.product === productName && c.plan_type === planType && c.price_amount === price
        );
      } else {
        // Legacy filter - just by product name
        customersToFilter = customersToFilter.filter(c => 
          c.product_id === productFilter || c.product === productFilter
        );
      }
    }
    
    // Apply plan type filter
    if (planTypeFilter) {
      customersToFilter = customersToFilter.filter(c => c.plan_type === planTypeFilter);
    }
    
    const filtered = filterAndSort(customersToFilter, ['email', 'name', 'product'], 'status');
    const availableStatuses = getManualStatuses();
    
    // Get unique product variants from existing manual customers (like Stripe filter)
    const productVariants = new Map();
    manualCustomers.forEach(c => {
      if (c.product && c.plan_type) {
        // Convert plan_type to interval format for consistency
        const interval = c.plan_type === 'monthly' ? 'month' : c.plan_type === 'yearly' ? 'year' : 'one_time';
        const amount = c.price_amount || 0;
        const key = `${c.product}|${interval}|${amount}`;
        if (!productVariants.has(key)) {
          productVariants.set(key, {
            name: c.product,
            interval: interval,
            amount: amount,
            key: key
          });
        }
      }
    });
    const availableVariants = Array.from(productVariants.values()).sort((a, b) => {
      // Sort by name first, then by interval
      const nameCompare = a.name.localeCompare(b.name);
      if (nameCompare !== 0) return nameCompare;
      // Sort order: month, year, one_time
      const intervalOrder = { 'month': 0, 'year': 1, 'one_time': 2 };
      return (intervalOrder[a.interval] || 3) - (intervalOrder[b.interval] || 3);
    });
    
    // Status labels in Italian
    const statusLabels = {
      active: 'Attivo',
      suspended: 'Sospeso',
      canceled: 'Cancellato'
    };
    
    // Plan type labels
    const planLabels = {
      monthly: 'Mensile',
      yearly: 'Annuale',
      one_time: 'Una tantum'
    };
    
    return (
      <div className="flex gap-6">
        {/* Main Table */}
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap gap-3 items-center justify-between mb-4">
            <div>
              <h2 className="text-xl font-bold text-white mb-1">Clienti Manuali</h2>
              <p className="text-slate-400 text-sm">Gestione clienti non registrati su Stripe</p>
            </div>
            <Button onClick={() => { setFormData({}); setEditMode(false); setSelectedManualCustomer(null); setShowCustomerModal(true); }} className="bg-emerald-500 hover:bg-emerald-400">
              <Plus className="w-4 h-4 mr-2" /> Nuovo Cliente
            </Button>
          </div>
          
          <div className="flex flex-wrap gap-3 items-center mb-4">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Cerca per email, nome..." className="w-full bg-slate-800/50 border border-slate-700 rounded-lg pl-10 pr-4 py-2.5 text-white text-sm focus:border-emerald-500 focus:outline-none" />
            </div>
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="bg-slate-800/50 border border-slate-700 rounded-lg px-4 py-2.5 text-white text-sm">
              <option value="">Tutti gli stati</option>
              {availableStatuses.map(status => (
                <option key={status} value={status}>{statusLabels[status] || status}</option>
              ))}
            </select>
            <select value={planTypeFilter} onChange={e => setPlanTypeFilter(e.target.value)} className="bg-slate-800/50 border border-slate-700 rounded-lg px-4 py-2.5 text-white text-sm">
              <option value="">Tutti i piani</option>
              <option value="monthly">Mensili</option>
              <option value="yearly">Annuali</option>
              <option value="one_time">Una tantum</option>
            </select>
            <select value={productFilter} onChange={e => setProductFilter(e.target.value)} className="bg-slate-800/50 border border-slate-700 rounded-lg px-4 py-2.5 text-white text-sm min-w-[220px]">
              <option value="">Tutti i prodotti</option>
              {availableVariants.map(variant => {
                const intervalLabel = variant.interval === 'month' ? 'Mensile' : 
                                     variant.interval === 'year' ? 'Annuale' : 'Una tantum';
                return (
                  <option key={variant.key} value={variant.key}>
                    {variant.name} - {intervalLabel} {variant.amount > 0 ? `(€${variant.amount})` : ''}
                  </option>
                );
              })}
            </select>
          </div>

          <div className="bg-slate-800/30 border border-slate-700 rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-700">
                    <th className="text-left p-4 text-xs text-slate-400 font-medium">Cliente</th>
                    <th className="text-left p-4 text-xs text-slate-400 font-medium">Nome Azienda</th>
                    <th className="text-left p-4 text-xs text-slate-400 font-medium cursor-pointer" onClick={() => handleSort('status')}>
                      <span className="flex items-center">Stato <SortIcon field="status" sortField={sortField} sortDirection={sortDirection} /></span>
                    </th>
                    <th className="text-left p-4 text-xs text-slate-400 font-medium">Prodotto/Piano</th>
                    <th className="text-left p-4 text-xs text-slate-400 font-medium cursor-pointer" onClick={() => handleSort('next_renewal')}>
                      <span className="flex items-center">Prossimo Rinnovo <SortIcon field="next_renewal" sortField={sortField} sortDirection={sortDirection} /></span>
                    </th>
                    <th className="text-left p-4 text-xs text-slate-400 font-medium cursor-pointer" onClick={() => handleSort('total_paid')}>
                      <span className="flex items-center">Totale Pagato <SortIcon field="total_paid" sortField={sortField} sortDirection={sortDirection} /></span>
                    </th>
                    <th className="text-left p-4 text-xs text-slate-400 font-medium cursor-pointer" onClick={() => handleSort('start_date')}>
                      <span className="flex items-center">Registrato <SortIcon field="start_date" sortField={sortField} sortDirection={sortDirection} /></span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(customer => {
                    const days = daysUntil(customer.next_renewal);
                    const isOverdue = days !== null && days < 0 && customer.plan_type !== 'one_time' && customer.status !== 'canceled';
                    return (
                      <tr 
                        key={customer.id} 
                        className={`border-b border-slate-700/50 hover:bg-slate-800/50 cursor-pointer transition-colors ${selectedManualCustomer === customer.id ? 'bg-slate-800/70' : ''} ${isOverdue ? 'bg-red-900/10' : ''}`}
                        onClick={() => setSelectedManualCustomer(customer.id)}
                      >
                        <td className="p-4">
                          <div className="flex items-center gap-3">
                            <div className="text-slate-400"><ChevronRight className="w-4 h-4" /></div>
                            <div>
                              <p className="text-white text-sm font-medium">{customer.email}</p>
                              <p className="text-slate-400 text-xs">{customer.name || 'N/A'}</p>
                            </div>
                          </div>
                        </td>
                        <td className="p-4">
                          <p className="text-emerald-400 text-sm font-medium">{customer.business_name || <span className="text-slate-500 italic">-</span>}</p>
                        </td>
                        <td className="p-4">
                          {isOverdue ? (
                            <span className="px-2.5 py-1 rounded-full text-xs font-medium border bg-red-500/20 text-red-400 border-red-500/30">In Ritardo</span>
                          ) : (
                            <StatusBadge status={customer.status} />
                          )}
                        </td>
                        <td className="p-4">
                          <div>
                            <p className="text-white text-sm">{customer.product || 'N/A'}</p>
                            <p className="text-slate-400 text-xs">
                              {planLabels[customer.plan_type] || customer.plan_type}
                              {customer.price_amount > 0 && (
                                <span className="text-emerald-400 ml-1">€{customer.price_amount}</span>
                              )}
                            </p>
                          </div>
                        </td>
                        <td className="p-4">
                          {customer.next_renewal && customer.plan_type !== 'one_time' ? (
                            <div>
                              <p className={`text-sm ${isOverdue ? 'text-red-400 font-medium' : 'text-white'}`}>{formatDate(customer.next_renewal)}</p>
                              {days !== null && days >= 0 && <p className="text-slate-400 text-xs">tra {days} giorni</p>}
                              {days !== null && days < 0 && <p className="text-red-400 text-xs font-medium">⚠️ {Math.abs(days)} giorni fa</p>}
                            </div>
                          ) : <span className="text-slate-500">-</span>}
                        </td>
                        <td className="p-4">
                          <div>
                            <p className="text-emerald-400 font-medium">€{customer.total_paid || 0}</p>
                            {customer.transactions_count > 0 && <p className="text-slate-400 text-xs">{customer.transactions_count} pagamenti</p>}
                          </div>
                        </td>
                        <td className="p-4 text-slate-300 text-sm">{formatDate(customer.start_date)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {filtered.length === 0 && <div className="p-8 text-center text-slate-500">Nessun cliente trovato</div>}
          </div>
        </div>

        {/* Right Sidebar - Manual Customer Detail */}
        {selectedManualCustomer && (
          <div className="w-96 flex-shrink-0">
            <div className="bg-slate-800/50 border border-slate-700 rounded-xl sticky top-24">
              <div className="flex items-center justify-between p-4 border-b border-slate-700">
                <h3 className="text-white font-semibold">Dettagli Cliente</h3>
                <div className="flex items-center gap-2">
                  {manualCustomerDetail?.customer && (
                    <button 
                      onClick={() => { 
                        setSelectedItem(manualCustomerDetail.customer); 
                        setFormData({
                          ...manualCustomerDetail.customer,
                          variant_id: manualCustomerDetail.customer.variant_id || ''
                        }); 
                        setEditMode(true); 
                        setShowCustomerModal(true); 
                      }} 
                      className="p-1 hover:bg-slate-700 rounded" 
                      title="Modifica"
                    >
                      <Edit2 className="w-4 h-4 text-amber-400" />
                    </button>
                  )}
                  <button onClick={() => { setSelectedManualCustomer(null); setManualCustomerDetail(null); }} className="p-1 hover:bg-slate-700 rounded">
                    <X className="w-4 h-4 text-slate-400" />
                  </button>
                </div>
              </div>
              
              {loadingManualDetail ? (
                <div className="p-8 text-center">
                  <RefreshCw className="w-6 h-6 text-emerald-500 animate-spin mx-auto" />
                </div>
              ) : manualCustomerDetail ? (
                <div className="p-4 space-y-4 max-h-[calc(100vh-200px)] overflow-y-auto">
                  
                  {/* VIEW MODE - Always show view mode now */}
                  <>
                    {/* Customer Info */}
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-slate-300">
                        <Mail className="w-4 h-4 text-slate-500" />
                        <span className="text-sm">{manualCustomerDetail.customer?.email}</span>
                      </div>
                      <div className="flex items-center gap-2 text-slate-300">
                        <User className="w-4 h-4 text-slate-500" />
                        <span className="text-sm">{manualCustomerDetail.customer?.name || 'N/A'}</span>
                      </div>
                      {manualCustomerDetail.customer?.phone && (
                        <div className="flex items-center gap-2 text-slate-300">
                          <Phone className="w-4 h-4 text-slate-500" />
                          <span className="text-sm">{manualCustomerDetail.customer.phone}</span>
                        </div>
                      )}
                      <div className="flex items-center gap-2 text-slate-300">
                        <Calendar className="w-4 h-4 text-slate-500" />
                        <span className="text-sm">Cliente dal {formatDate(manualCustomerDetail.customer?.start_date)}</span>
                      </div>
                    </div>

                    {/* Total Spent */}
                    <div className="pt-2 border-t border-slate-700">
                      <p className="text-slate-400 text-xs mb-1">Totale Pagato</p>
                      <p className="text-2xl font-bold text-emerald-400">€{manualCustomerDetail.total_paid || 0}</p>
                    </div>

                    {/* Product & Plan Info */}
                    <div className="pt-2 border-t border-slate-700">
                      <p className="text-slate-400 text-xs mb-2">Abbonamento</p>
                      {(() => {
                        const customer = manualCustomerDetail.customer;
                        const transactions = manualCustomerDetail.transactions || [];
                        const nextRenewal = getNextRenewal(customer, transactions);
                        const overdue = isPaymentOverdue(customer, transactions);
                        
                        return (
                          <div className={`rounded-lg p-3 ${overdue ? 'bg-red-900/30 border border-red-500/30' : 'bg-slate-900/50'}`}>
                            <div className="flex items-start justify-between mb-1">
                              <p className="text-white text-sm font-medium">{customer?.product || 'N/A'}</p>
                              {overdue ? (
                                <span className="px-2.5 py-1 rounded-full text-xs font-medium border bg-red-500/20 text-red-400 border-red-500/30">In Ritardo</span>
                              ) : (
                                <StatusBadge status={customer?.status} />
                              )}
                            </div>
                            <p className="text-slate-400 text-xs">
                              {planLabels[customer?.plan_type] || 'N/A'}
                              {customer?.price_amount > 0 && <span className="text-emerald-400 ml-1">€{customer.price_amount}</span>}
                            </p>
                            {customer?.start_date && (
                              <p className="text-slate-500 text-xs mt-1">Inizio: {formatDate(customer.start_date)}</p>
                            )}
                            {nextRenewal && customer?.plan_type !== 'one_time' && (
                              <p className={`text-xs mt-1 ${overdue ? 'text-red-400 font-medium' : 'text-slate-500'}`}>
                                {overdue ? '⚠️ Scaduto il: ' : 'Prossimo rinnovo: '}{formatDate(nextRenewal)}
                                {overdue && ` (${Math.abs(daysUntil(nextRenewal))} giorni fa)`}
                              </p>
                            )}
                          </div>
                        );
                      })()}
                    </div>

                    {/* Payment Method */}
                    <div className="pt-2 border-t border-slate-700">
                      <p className="text-slate-400 text-xs mb-2">Metodo di Pagamento</p>
                      <div className="flex items-center gap-2 text-slate-300 text-sm">
                        <CreditCard className="w-4 h-4 text-slate-500" />
                        <span className="capitalize">{manualCustomerDetail.customer?.payment_method || 'N/A'}</span>
                      </div>
                    </div>

                    {/* Notes */}
                    {manualCustomerDetail.customer?.notes && (
                      <div className="pt-2 border-t border-slate-700">
                        <p className="text-slate-400 text-xs mb-2">Note</p>
                        <p className="text-slate-300 text-sm">{manualCustomerDetail.customer.notes}</p>
                      </div>
                    )}

                    {/* Transactions */}
                    <div className="pt-2 border-t border-slate-700">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-slate-400 text-xs">Transazioni ({manualCustomerDetail.transactions?.length || 0})</p>
                        <button 
                          onClick={() => { 
                            const customer = manualCustomerDetail.customer;
                            const todayStr = new Date().toISOString().split('T')[0];
                            const currentRenewal = customer.next_renewal?.split('T')[0];
                            const isRenewalPayment = currentRenewal && todayStr === currentRenewal;
                            const suggestedRenewal = customer.plan_type !== 'one_time' 
                              ? calculateNextRenewalFromDate(todayStr, customer.plan_type) 
                              : null;
                            
                            setSelectedItem(customer); 
                            setFormData({ 
                              customer_id: customer.id, 
                              amount: '', 
                              payment_method: customer.payment_method,
                              date: todayStr,
                              update_renewal: isRenewalPayment,
                              new_renewal_date: suggestedRenewal,
                              _isRenewalPayment: isRenewalPayment
                            }); 
                            setShowTransactionModal(true); 
                          }}
                          className="text-emerald-400 hover:text-emerald-300 text-xs flex items-center gap-1"
                        >
                          <Plus className="w-3 h-3" /> Aggiungi
                        </button>
                      </div>
                      {manualCustomerDetail.transactions?.length > 0 ? (
                        <div className="space-y-1.5 max-h-48 overflow-y-auto">
                            {manualCustomerDetail.transactions.map((trans) => (
                              <div key={trans.id} className="flex items-center justify-between py-1.5 px-2 bg-slate-900/30 rounded group">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2">
                                    <span className="text-white text-sm font-medium">€{trans.amount}</span>
                                    <span className="text-slate-500 text-xs">{formatDate(trans.date)}</span>
                                  </div>
                                  {trans.description && <p className="text-slate-500 text-xs">{trans.description}</p>}
                                </div>
                                <div className="flex items-center gap-1">
                                  <span className="px-2 py-0.5 rounded text-xs bg-emerald-500/20 text-emerald-400 capitalize">{trans.payment_method}</span>
                                  <button 
                                    onClick={(e) => { e.stopPropagation(); deleteTransaction(trans.id); }}
                                    disabled={deletingTransaction === trans.id}
                                    className="p-1 opacity-0 group-hover:opacity-100 hover:bg-red-500/20 rounded transition-all disabled:opacity-100"
                                    title="Elimina transazione"
                                  >
                                    {deletingTransaction === trans.id ? (
                                      <RefreshCw className="w-3 h-3 text-red-400 animate-spin" />
                                    ) : (
                                      <Trash2 className="w-3 h-3 text-red-400" />
                                    )}
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-slate-500 text-xs">Nessuna transazione</p>
                        )}
                      </div>

                    {/* Actions */}
                    <div className="pt-2 border-t border-slate-700">
                      <button 
                        onClick={() => deleteManualCustomer(selectedManualCustomer)}
                        disabled={deletingCustomer}
                        className="flex items-center justify-center gap-2 w-full py-2 text-sm text-red-400 hover:text-red-300 bg-red-500/10 hover:bg-red-500/20 rounded-lg transition-colors disabled:opacity-50"
                      >
                        {deletingCustomer ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                        {deletingCustomer ? 'Eliminazione...' : 'Elimina Cliente'}
                      </button>
                    </div>
                  </>
                </div>
              ) : null}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Configurations Tab
  const renderConfigurations = () => {
    const filtered = filterAndSort(configurations, ['customer_email', 'customer_name', 'business_name']);
    return (
      <div className="space-y-4">
        <div className="flex flex-wrap gap-3 items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-white mb-1">Configurazioni</h2>
            <p className="text-slate-400 text-sm">Richieste di configurazione da completare</p>
          </div>
          <Button onClick={() => { setFormData({ status: 'pending' }); setEditMode(false); setConfigStep(1); setSelectedModules([]); setConfigServices([]); setConfigProducts([]); setConfigCourses([]); setConfigEvents([]); setConfigOperators([]); setShowConfigModal(true); }} className="bg-emerald-500 hover:bg-emerald-400">
            <Plus className="w-4 h-4 mr-2" /> Nuova Configurazione
          </Button>
        </div>

        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[250px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Cerca..." className="w-full bg-slate-800/50 border border-slate-700 rounded-lg pl-10 pr-4 py-2.5 text-white text-sm" />
          </div>
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="bg-slate-800/50 border border-slate-700 rounded-lg px-4 py-2.5 text-white text-sm">
            <option value="">Tutti gli stati</option>
            <option value="pending">Da fare</option>
            <option value="completed">Completate</option>
            <option value="canceled">Cancellate</option>
          </select>
        </div>

        <div className="bg-slate-800/30 border border-slate-700 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-700">
                  <th className="text-left p-4 text-xs text-slate-400 font-medium">Attività</th>
                  <th className="text-left p-4 text-xs text-slate-400 font-medium">Cliente</th>
                  <th className="text-left p-4 text-xs text-slate-400 font-medium">Email</th>
                  <th className="text-left p-4 text-xs text-slate-400 font-medium">Telefono</th>
                  <th className="text-left p-4 text-xs text-slate-400 font-medium">Data</th>
                  <th className="text-left p-4 text-xs text-slate-400 font-medium">Stato</th>
                  <th className="text-left p-4 text-xs text-slate-400 font-medium">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(config => (
                  <tr key={config.id} className="border-b border-slate-700/50 hover:bg-slate-800/50">
                    <td className="p-4 text-white text-sm font-medium">{config.business_name || 'N/A'}</td>
                    <td className="p-4 text-slate-300 text-sm">{config.customer_name || 'N/A'}</td>
                    <td className="p-4 text-slate-300 text-sm">{config.customer_email}</td>
                    <td className="p-4 text-slate-300 text-sm">{config.customer_phone || 'N/A'}</td>
                    <td className="p-4 text-slate-300 text-sm">{formatDate(config.created_at)}</td>
                    <td className="p-4"><StatusBadge status={config.status} /></td>
                    <td className="p-4">
                      <div className="flex gap-1">
                        {config.status === 'pending' && (
                          <button onClick={() => updateConfigStatus(config.id, 'completed')} className="p-1.5 hover:bg-slate-700 rounded-lg" title="Completa"><CheckCircle className="w-4 h-4 text-emerald-400" /></button>
                        )}
                        {config.status !== 'canceled' && (
                          <button onClick={() => updateConfigStatus(config.id, 'canceled')} className="p-1.5 hover:bg-slate-700 rounded-lg" title="Cancella"><XCircle className="w-4 h-4 text-red-400" /></button>
                        )}
                        <button onClick={() => { 
                          setSelectedItem(config); 
                          setFormData(config); 
                          setEditMode(true); 
                          setConfigStep(1);
                          setWizardErrors({});
                          setHasVat(config.hasVat || !!config.vat_number);
                          setOpeningHours(config.openingHours || []);
                          const detectedModules = config.modules || [];
                          if (detectedModules.length === 0) {
                            if (config.services?.length > 0) detectedModules.push('appointments');
                            if (config.products?.length > 0) detectedModules.push('ecommerce');
                            if (config.courses?.length > 0) detectedModules.push('courses');
                            if (config.events?.length > 0) detectedModules.push('events');
                          }
                          setSelectedModules(detectedModules);
                          setConfigStaff(config.staff || []);
                          setConfigServices(config.services || []);
                          setConfigProducts(config.products || []);
                          setConfigCourses(config.courses || []);
                          setConfigEvents(config.events || []);
                          setConfigSubscriptions(config.subscriptions || []);
                          setShowConfigModal(true); 
                        }} className="p-1.5 hover:bg-slate-700 rounded-lg" title="Modifica"><Edit2 className="w-4 h-4 text-amber-400" /></button>
                        <button onClick={() => deleteConfiguration(config.id)} className="p-1.5 hover:bg-slate-700 rounded-lg" title="Elimina"><Trash2 className="w-4 h-4 text-red-400" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {filtered.length === 0 && <div className="p-8 text-center text-slate-500">Nessuna configurazione trovata</div>}
        </div>
      </div>
    );
  };

  // Products Tab
  const renderProducts = () => (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-3 items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-bold text-white mb-1">Prodotti</h2>
          <p className="text-slate-400 text-sm">Prodotti configurati su Stripe ({products.length} totali, {firebaseProducts.length} sincronizzati)</p>
        </div>
        <Button 
          onClick={syncProductsFromStripe} 
          disabled={syncingProducts}
          className="bg-blue-500 hover:bg-blue-400 disabled:opacity-50 flex items-center gap-2"
        >
          {syncingProducts ? <RefreshCw className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          {syncingProducts ? 'Sincronizzazione...' : 'Sincronizza da Stripe'}
        </Button>
      </div>
      
      {/* Synced products info */}
      {firebaseProducts.length > 0 && (
        <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-3 mb-4">
          <p className="text-emerald-400 text-sm">
            <CheckCircle className="w-4 h-4 inline mr-2" />
            {firebaseProducts.length} prodotti sincronizzati e disponibili per i clienti manuali
          </p>
        </div>
      )}
      
      {firebaseProducts.length === 0 && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 mb-4">
          <p className="text-amber-400 text-sm">
            <AlertCircle className="w-4 h-4 inline mr-2" />
            Nessun prodotto sincronizzato. Clicca il pulsante per importare i prodotti da Stripe.
          </p>
        </div>
      )}
      
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {products.map(product => {
          const isSynced = firebaseProducts.find(fp => fp.stripe_id === product.id);
          return (
            <div key={product.id} className={`bg-slate-800/50 border rounded-xl p-4 ${isSynced ? 'border-emerald-500/30' : 'border-slate-700'}`}>
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h4 className="text-white font-semibold flex items-center gap-2">
                    {product.name}
                    {isSynced && <CheckCircle className="w-4 h-4 text-emerald-400" />}
                  </h4>
                  <p className="text-slate-400 text-sm">{product.description || 'Nessuna descrizione'}</p>
                </div>
                <span className={`px-2 py-1 rounded text-xs ${product.active ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-500/20 text-slate-400'}`}>
                  {product.active ? 'Attivo' : 'Inattivo'}
                </span>
              </div>
              {product.prices?.length > 0 && (
                <div className="space-y-2 pt-2 border-t border-slate-700">
                  {product.prices.map(price => (
                    <div key={price.id} className="flex justify-between text-sm">
                      <span className="text-slate-400">{price.interval === 'month' ? 'Mensile' : price.interval === 'year' ? 'Annuale' : price.interval}</span>
                      <span className="text-white font-medium">€{price.amount}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );

  // ==================== MODALS ====================

  // Modal for editing Stripe customer local data
  const renderStripeCustomerEditModal = () => {
    if (!showStripeCustomerEditModal) return null;
    
    return (
      <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
        <div className="bg-slate-800 border border-slate-700 rounded-xl w-full max-w-md">
          <div className="flex items-center justify-between p-4 border-b border-slate-700">
            <h3 className="text-white font-semibold">Modifica Dati Cliente</h3>
            <button 
              onClick={() => { setShowStripeCustomerEditModal(false); setEditingStripeCustomer(null); }}
              className="p-1 hover:bg-slate-700 rounded text-slate-400 hover:text-white"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="p-4 space-y-4">
            <div>
              <label className="block text-slate-400 text-sm mb-1">Nome Azienda</label>
              <input
                type="text"
                value={stripeCustomerFormData.company_name || ''}
                onChange={e => setStripeCustomerFormData({ ...stripeCustomerFormData, company_name: e.target.value })}
                placeholder="Inserisci il nome dell'azienda"
                className="w-full bg-slate-900/50 border border-slate-600 rounded-lg px-4 py-2.5 text-white focus:border-emerald-500 focus:outline-none"
              />
            </div>
            <p className="text-slate-500 text-xs">
              Questi dati sono memorizzati localmente e non vengono sincronizzati con Stripe.
            </p>
          </div>
          <div className="flex justify-end gap-3 p-4 border-t border-slate-700">
            <Button 
              variant="outline" 
              onClick={() => { setShowStripeCustomerEditModal(false); setEditingStripeCustomer(null); }}
              className="border-slate-600 text-slate-300"
            >
              Annulla
            </Button>
            <Button 
              onClick={() => updateStripeCustomerLocal(editingStripeCustomer, stripeCustomerFormData)}
              disabled={savingStripeCustomer}
              className="bg-emerald-500 hover:bg-emerald-400"
            >
              {savingStripeCustomer ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : null}
              Salva
            </Button>
          </div>
        </div>
      </div>
    );
  };

  const renderCustomerModal = () => {
    // Build flat list of product variants (product + price combination)
    const productVariants = [];
    firebaseProducts.forEach(prod => {
      if (prod.prices && prod.prices.length > 0) {
        prod.prices.forEach(price => {
          const intervalLabel = price.interval === 'month' ? 'Mensile' : 
                               price.interval === 'year' ? 'Annuale' : 
                               price.interval === 'one_time' ? 'Una tantum' : price.interval;
          const planType = price.interval === 'month' ? 'monthly' : 
                          price.interval === 'year' ? 'yearly' : 'one_time';
          productVariants.push({
            id: `${prod.id}_${price.id}`,
            product_id: prod.id,
            product_name: prod.name,
            price_id: price.id,
            price_amount: price.amount,
            plan_type: planType,
            label: `${prod.name} - ${intervalLabel} (€${price.amount})`
          });
        });
      } else {
        // Product without prices
        productVariants.push({
          id: prod.id,
          product_id: prod.id,
          product_name: prod.name,
          price_id: null,
          price_amount: 0,
          plan_type: 'monthly',
          label: `${prod.name} (prezzo non definito)`
        });
      }
    });

    return (
      <Modal isOpen={showCustomerModal} onClose={() => setShowCustomerModal(false)} title={editMode ? 'Modifica Cliente' : 'Nuovo Cliente Manuale'}>
        <div className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <FormInput label="Nome" value={formData.name || ''} onChange={e => setFormData({...formData, name: e.target.value})} required />
            <FormInput label="Email" type="email" value={formData.email || ''} onChange={e => setFormData({...formData, email: e.target.value})} required />
            <FormInput label="Nome Azienda" value={formData.business_name || ''} onChange={e => setFormData({...formData, business_name: e.target.value})} />
            <FormInput label="Telefono" value={formData.phone || ''} onChange={e => setFormData({...formData, phone: e.target.value})} />
            <FormInput label="Metodo Pagamento" type="select" value={formData.payment_method || 'bonifico'} onChange={e => setFormData({...formData, payment_method: e.target.value})} options={[{value: 'bonifico', label: 'Bonifico'}, {value: 'contanti', label: 'Contanti'}, {value: 'carta', label: 'Carta'}, {value: 'altro', label: 'Altro'}]} />
          </div>
          
          {/* Product Selection - Full Width */}
          <div className="space-y-1">
            <label className="text-sm text-slate-400">Prodotto e Piano <span className="text-red-400">*</span></label>
            <select 
              value={formData.variant_id || ''} 
              onChange={e => {
                const variant = productVariants.find(v => v.id === e.target.value);
                if (variant) {
                  setFormData({
                    ...formData, 
                    variant_id: variant.id,
                    product_id: variant.product_id,
                    product: variant.product_name,
                    price_id: variant.price_id,
                    price_amount: variant.price_amount,
                    plan_type: variant.plan_type
                  });
                }
              }}
              className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2.5 text-white focus:border-emerald-500 focus:outline-none"
            >
              <option value="">Seleziona prodotto e piano...</option>
              {productVariants.map(variant => (
                <option key={variant.id} value={variant.id}>{variant.label}</option>
              ))}
            </select>
            {firebaseProducts.length === 0 && (
              <p className="text-amber-400 text-xs mt-1">Nessun prodotto trovato. Vai su Prodotti e sincronizza da Stripe.</p>
            )}
          </div>
          
          {/* Show selected product info */}
          {formData.product && (
            <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-3 flex items-center justify-between">
              <div>
                <p className="text-white font-medium">{formData.product}</p>
                <p className="text-slate-400 text-sm">
                  {formData.plan_type === 'monthly' ? 'Mensile' : formData.plan_type === 'yearly' ? 'Annuale' : 'Una tantum'}
                </p>
              </div>
              {formData.price_amount > 0 && (
                <p className="text-emerald-400 font-bold text-lg">€{formData.price_amount}</p>
              )}
            </div>
          )}
          
          <div className="grid md:grid-cols-2 gap-4">
            {editMode && (
              <FormInput label="Stato" type="select" value={formData.status || 'active'} onChange={e => setFormData({...formData, status: e.target.value})} options={[{value: 'active', label: 'Attivo'}, {value: 'suspended', label: 'Sospeso'}, {value: 'canceled', label: 'Cancellato'}]} />
            )}
            <FormInput label="Data Inizio" type="date" value={formData.start_date?.split('T')[0] || ''} onChange={e => setFormData({...formData, start_date: e.target.value})} />
          </div>
          <FormInput label="Note" type="textarea" value={formData.notes || ''} onChange={e => setFormData({...formData, notes: e.target.value})} />
          <div className="flex justify-end gap-3 pt-4">
            <Button variant="outline" onClick={() => setShowCustomerModal(false)} disabled={savingCustomer}>Annulla</Button>
            <Button onClick={saveManualCustomer} disabled={savingCustomer} className="bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 flex items-center gap-2">
              {savingCustomer && <RefreshCw className="w-4 h-4 animate-spin" />}
              {savingCustomer ? 'Salvataggio...' : (editMode ? 'Salva' : 'Crea')}
            </Button>
          </div>
        </div>
      </Modal>
    );
  };

  const renderTransactionModal = () => (
    <Modal isOpen={showTransactionModal} onClose={() => setShowTransactionModal(false)} title="Aggiungi Transazione">
      <div className="space-y-4">
        <p className="text-slate-400 text-sm">Cliente: <span className="text-white">{selectedItem?.name}</span></p>
        
        {/* Current renewal info */}
        {selectedItem?.plan_type !== 'one_time' && selectedItem?.next_renewal && (
          <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-3">
            <p className="text-slate-400 text-xs">Prossimo rinnovo attuale:</p>
            <p className="text-white text-sm font-medium">{formatDate(selectedItem.next_renewal)}</p>
          </div>
        )}
        
        <div className="grid md:grid-cols-2 gap-4">
          <FormInput label="Importo (€)" type="number" value={formData.amount || ''} onChange={e => setFormData({...formData, amount: e.target.value})} required />
          <FormInput label="Data Pagamento" type="date" value={formData.date?.split('T')[0] || new Date().toISOString().split('T')[0]} onChange={e => {
            const newDate = e.target.value;
            const customer = selectedItem;
            const currentRenewal = customer?.next_renewal?.split('T')[0];
            
            // Check if transaction date matches current renewal date
            const isRenewalPayment = currentRenewal && newDate === currentRenewal;
            
            // Calculate suggested new renewal
            let suggestedRenewal = null;
            if (customer?.plan_type !== 'one_time') {
              suggestedRenewal = calculateNextRenewalFromDate(newDate, customer?.plan_type);
            }
            
            setFormData({
              ...formData, 
              date: newDate,
              // Auto-enable renewal update if date matches
              update_renewal: isRenewalPayment,
              new_renewal_date: suggestedRenewal,
              _isRenewalPayment: isRenewalPayment
            });
          }} />
          <FormInput label="Metodo" type="select" value={formData.payment_method || 'bonifico'} onChange={e => setFormData({...formData, payment_method: e.target.value})} options={[{value: 'bonifico', label: 'Bonifico'}, {value: 'contanti', label: 'Contanti'}, {value: 'carta', label: 'Carta'}, {value: 'altro', label: 'Altro'}]} />
          <FormInput label="Prodotto" value={formData.product || selectedItem?.product || ''} onChange={e => setFormData({...formData, product: e.target.value})} />
        </div>
        <FormInput label="Descrizione" value={formData.description || 'Pagamento abbonamento'} onChange={e => setFormData({...formData, description: e.target.value})} />
        
        {/* Renewal update section - only for subscriptions */}
        {selectedItem?.plan_type !== 'one_time' && (
          <div className={`border rounded-lg p-4 ${formData._isRenewalPayment ? 'bg-emerald-900/20 border-emerald-500/30' : 'bg-slate-800/30 border-slate-700'}`}>
            {formData._isRenewalPayment ? (
              <>
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle className="w-4 h-4 text-emerald-400" />
                  <p className="text-emerald-400 text-sm font-medium">Pagamento rinnovo rilevato!</p>
                </div>
                <p className="text-slate-400 text-xs mb-3">La data corrisponde al rinnovo attuale. La nuova data di rinnovo sarà aggiornata automaticamente.</p>
              </>
            ) : (
              <>
                <p className="text-slate-400 text-xs mb-3">La data non corrisponde al rinnovo. Vuoi comunque aggiornare la data di rinnovo?</p>
              </>
            )}
            
            <div className="flex items-center gap-3 mb-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input 
                  type="checkbox" 
                  checked={formData.update_renewal || false} 
                  onChange={e => setFormData({...formData, update_renewal: e.target.checked})}
                  className="w-4 h-4 rounded border-slate-600 bg-slate-800 text-emerald-500 focus:ring-emerald-500"
                />
                <span className="text-white text-sm">Aggiorna data rinnovo</span>
              </label>
            </div>
            
            {formData.update_renewal && (
              <div>
                <label className="text-slate-400 text-xs">Nuova data rinnovo</label>
                <input 
                  type="date" 
                  value={formData.new_renewal_date || ''} 
                  onChange={e => setFormData({...formData, new_renewal_date: e.target.value})}
                  className="w-full bg-slate-900 border border-slate-600 rounded px-3 py-2 text-white text-sm mt-1 focus:border-emerald-500 focus:outline-none"
                />
              </div>
            )}
          </div>
        )}
        
        <div className="flex justify-end gap-3 pt-4">
          <Button variant="outline" onClick={() => setShowTransactionModal(false)} disabled={savingTransaction}>Annulla</Button>
          <Button onClick={saveTransaction} disabled={savingTransaction} className="bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 flex items-center gap-2">
            {savingTransaction && <RefreshCw className="w-4 h-4 animate-spin" />}
            {savingTransaction ? 'Salvataggio...' : 'Aggiungi'}
          </Button>
        </div>
      </div>
    </Modal>
  );

  const renderConfigModal = () => {
    const GIORNI = ['Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì', 'Sabato', 'Domenica'];
    const totalSteps = 2 + selectedModules.length + 1;
    const isLastStep = configStep === totalSteps;
    
    const resetConfigWizard = () => {
      setShowConfigModal(false);
      setConfigStep(1);
      setSelectedModules([]);
      setHasVat(false);
      setOpeningHours([]);
      setConfigStaff([]);
      setConfigServices([]);
      setConfigProducts([]);
      setConfigCourses([]);
      setConfigEvents([]);
      setConfigSubscriptions([]);
      setFormData({});
      setEditMode(false);
      setWizardErrors({});
    };

    const validateStep = (step) => {
      const errors = {};
      if (step === 1) {
        if (!formData.customer_name?.trim()) errors.customer_name = 'Campo obbligatorio';
        if (!formData.customer_email?.trim()) errors.customer_email = 'Campo obbligatorio';
        if (!formData.business_name?.trim()) errors.business_name = 'Campo obbligatorio';
      }
      if (step === 2 && selectedModules.length === 0) errors.modules = 'Seleziona almeno un modulo';
      if (step > 2 && step <= 2 + selectedModules.length) {
        const mod = selectedModules[step - 3];
        if (mod === 'appointments') configServices.forEach((s, i) => { if (!s.name?.trim()) errors[`service_name_${i}`] = 'Nome obbligatorio'; });
        if (mod === 'ecommerce') configProducts.forEach((p, i) => { if (!p.name?.trim()) errors[`product_name_${i}`] = 'Nome obbligatorio'; });
        if (mod === 'courses') configCourses.forEach((c, i) => { if (!c.name?.trim()) errors[`course_name_${i}`] = 'Nome obbligatorio'; });
        if (mod === 'events') configEvents.forEach((ev, i) => { if (!ev.name?.trim()) errors[`event_name_${i}`] = 'Nome obbligatorio'; });
      }
      setWizardErrors(errors);
      return Object.keys(errors).length === 0;
    };

    const handleNextStep = () => { if (validateStep(configStep)) setConfigStep(configStep + 1); };

    // Opening Hours
    const addOpeningHour = () => setOpeningHours([...openingHours, { id: Date.now(), day: 'Lunedì', open: '09:00', close: '18:00' }]);
    const updateOpeningHour = (id, field, val) => setOpeningHours(openingHours.map(h => h.id === id ? { ...h, [field]: val } : h));
    const removeOpeningHour = (id) => setOpeningHours(openingHours.filter(h => h.id !== id));

    // Staff
    const addStaff = () => setConfigStaff([...configStaff, { id: Date.now(), name: '', workingHours: [{ id: Date.now() + 1, day: 'Lunedì', start: '09:00', end: '18:00' }] }]);
    const updateStaff = (id, field, val) => setConfigStaff(configStaff.map(s => s.id === id ? { ...s, [field]: val } : s));
    const removeStaff = (id) => setConfigStaff(configStaff.filter(s => s.id !== id));
    const addStaffHour = (staffId) => setConfigStaff(configStaff.map(s => s.id === staffId ? { ...s, workingHours: [...s.workingHours, { id: Date.now(), day: 'Lunedì', start: '09:00', end: '18:00' }] } : s));
    const updateStaffHour = (staffId, hourId, field, val) => setConfigStaff(configStaff.map(s => s.id === staffId ? { ...s, workingHours: s.workingHours.map(h => h.id === hourId ? { ...h, [field]: val } : h) } : s));
    const removeStaffHour = (staffId, hourId) => setConfigStaff(configStaff.map(s => s.id === staffId ? { ...s, workingHours: s.workingHours.filter(h => h.id !== hourId) } : s));

    // Services
    const addService = () => setConfigServices([...configServices, { id: Date.now(), name: '', duration: 30, price: 0, priceType: 'fixed', priceMin: 0, priceMax: 0, operators_text: '' }]);
    const updateService = (id, field, value) => setConfigServices(configServices.map(s => s.id === id ? { ...s, [field]: value } : s));
    const removeService = (id) => setConfigServices(configServices.filter(s => s.id !== id));

    const addProduct = () => setConfigProducts([...configProducts, { id: Date.now(), name: '', price: 0, quantity: 1, category: '', variants: '' }]);
    const updateProduct = (id, field, value) => setConfigProducts(configProducts.map(p => p.id === id ? { ...p, [field]: value } : p));
    const removeProduct = (id) => setConfigProducts(configProducts.filter(p => p.id !== id));

    const addCourse = () => setConfigCourses([...configCourses, { id: Date.now(), name: '', hourlyDuration: '1', day: 'Lunedì', startTime: '09:00', instructor: '', price: 0, maxParticipants: 10, linkedSubscription: '' }]);
    const updateCourse = (id, field, value) => setConfigCourses(configCourses.map(c => c.id === id ? { ...c, [field]: value } : c));
    const removeCourse = (id) => setConfigCourses(configCourses.filter(c => c.id !== id));

    const addEvent = () => setConfigEvents([...configEvents, { id: Date.now(), name: '', date: '', time: '', price: 0, maxParticipants: 50, description: '' }]);
    const updateEvent = (id, field, value) => setConfigEvents(configEvents.map(e => e.id === id ? { ...e, [field]: value } : e));
    const removeEvent = (id) => setConfigEvents(configEvents.filter(e => e.id !== id));

    const addSubscription = () => setConfigSubscriptions([...configSubscriptions, { id: Date.now(), name: '', price: 0, duration: '1 mese' }]);
    const updateSubscription = (id, field, val) => setConfigSubscriptions(configSubscriptions.map(s => s.id === id ? { ...s, [field]: val } : s));
    const removeSubscription = (id) => setConfigSubscriptions(configSubscriptions.filter(s => s.id !== id));

    const toggleModule = (moduleId) => {
      setSelectedModules(prev => prev.includes(moduleId) ? prev.filter(m => m !== moduleId) : [...prev, moduleId]);
    };

    const handleSaveConfig = async () => {
      if (!validateStep(configStep)) return;
      
      const configData = {
        ...formData,
        hasVat, openingHours,
        modules: selectedModules,
        staff: selectedModules.includes('appointments') ? configStaff : [],
        services: selectedModules.includes('appointments') ? configServices : [],
        products: selectedModules.includes('ecommerce') ? configProducts : [],
        courses: selectedModules.includes('courses') ? configCourses : [],
        subscriptions: selectedModules.includes('courses') ? configSubscriptions : [],
        events: selectedModules.includes('events') ? configEvents : [],
        status: formData.status || 'pending'
      };
      
      setSavingConfig(true);
      try {
        if (editMode && selectedItem) {
          await postWithAuth(`/api/admin/configurations/${selectedItem.id}`, configData, 'PUT');
        } else {
          await postWithAuth('/api/admin/configurations', configData);
        }
        resetConfigWizard();
        await loadConfigurations();
        await loadDashboard();
      } catch (err) {
        alert('Errore: ' + err.message);
      } finally {
        setSavingConfig(false);
      }
    };

    const getStepForModule = (moduleIndex) => 3 + moduleIndex;

    const getCurrentModuleStep = () => {
      if (configStep <= 2 || configStep > 2 + selectedModules.length) return null;
      return selectedModules[configStep - 3];
    };

    const moduleLabels = {
      appointments: 'Servizi a Prenotazione',
      ecommerce: 'E-commerce / Prodotti',
      courses: 'Corsi',
      events: 'Eventi'
    };

    const moduleIcons = {
      appointments: <Calendar className="w-5 h-5" />,
      ecommerce: <Package className="w-5 h-5" />,
      courses: <FileText className="w-5 h-5" />,
      events: <Calendar className="w-5 h-5" />
    };

    return (
      <Modal isOpen={showConfigModal} onClose={resetConfigWizard} title={editMode ? 'Modifica Configurazione' : 'Nuova Configurazione'} size="lg">
        {/* Progress Steps */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            {[...Array(Math.max(2, totalSteps))].map((_, idx) => {
              const stepNum = idx + 1;
              let stepLabel = '';
              if (stepNum === 1) stepLabel = 'Dati Azienda';
              else if (stepNum === 2) stepLabel = 'Moduli';
              else if (stepNum > 2 && selectedModules[stepNum - 3]) stepLabel = moduleLabels[selectedModules[stepNum - 3]];
              
              return (
                <div key={idx} className="flex items-center flex-1">
                  <div className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-semibold transition-colors ${
                    configStep > stepNum ? 'bg-emerald-500 text-white' : 
                    configStep === stepNum ? 'bg-emerald-500 text-white' : 
                    'bg-slate-700 text-slate-400'
                  }`}>
                    {configStep > stepNum ? <CheckCircle className="w-5 h-5" /> : stepNum}
                  </div>
                  {idx < Math.max(1, totalSteps - 1) && (
                    <div className={`flex-1 h-1 mx-2 rounded ${configStep > stepNum ? 'bg-emerald-500' : 'bg-slate-700'}`}></div>
                  )}
                </div>
              );
            })}
          </div>
          <p className="text-center text-slate-400 text-sm">
            {configStep === 1 && 'Inserisci i dati dell\'azienda e del cliente'}
            {configStep === 2 && 'Seleziona i moduli che l\'attività vuole attivare'}
            {configStep > 2 && configStep <= 2 + selectedModules.length && `Configura: ${moduleLabels[getCurrentModuleStep()] || ''}`}
            {isLastStep && 'Presenza online e note finali'}
          </p>
        </div>

        <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
          {/* STEP 1: Dati Azienda */}
          {configStep === 1 && (
            <>
              <h4 className="text-white font-semibold flex items-center gap-2">
                <User className="w-4 h-4 text-emerald-400" /> Dati Cliente
              </h4>
              <p className="text-slate-500 text-xs mb-2">Informazioni del referente principale dell'attività</p>
              <div className="grid md:grid-cols-3 gap-4">
                <FormInput label="Nome e Cognome" value={formData.customer_name || ''} onChange={e => setFormData({...formData, customer_name: e.target.value})} placeholder="Es. Mario Rossi" required error={wizardErrors.customer_name} />
                <FormInput label="Email" type="email" value={formData.customer_email || ''} onChange={e => setFormData({...formData, customer_email: e.target.value})} placeholder="email@esempio.it" required error={wizardErrors.customer_email} />
                <FormInput label="Telefono" value={formData.customer_phone || ''} onChange={e => setFormData({...formData, customer_phone: e.target.value})} placeholder="+39 333 1234567" />
              </div>
              
              <h4 className="text-white font-semibold pt-4 flex items-center gap-2">
                <Package className="w-4 h-4 text-amber-400" /> Dati Attività
              </h4>
              <p className="text-slate-500 text-xs mb-2">Informazioni sulla sede e tipologia di attività</p>
              <div className="grid md:grid-cols-2 gap-4">
                <FormInput label="Nome Attività" value={formData.business_name || ''} onChange={e => setFormData({...formData, business_name: e.target.value})} placeholder="Es. Salone Bellezza Anna" required error={wizardErrors.business_name} />
                <FormInput label="Tipo Attività" value={formData.business_type || ''} onChange={e => setFormData({...formData, business_type: e.target.value})} placeholder="Es. Parrucchiere, Ristorante, SPA..." />
                <FormInput label="Indirizzo Sede" value={formData.business_address || ''} onChange={e => setFormData({...formData, business_address: e.target.value})} placeholder="Via Roma 1" />
                <FormInput label="Città" value={formData.business_city || ''} onChange={e => setFormData({...formData, business_city: e.target.value})} placeholder="Es. Napoli" />
                <FormInput label="CAP" value={formData.business_cap || ''} onChange={e => setFormData({...formData, business_cap: e.target.value})} placeholder="Es. 80100" />
                <FormInput label="Provincia" value={formData.business_province || ''} onChange={e => setFormData({...formData, business_province: e.target.value})} placeholder="Es. NA" />
              </div>

              <h4 className="text-white font-semibold pt-4 flex items-center gap-2">
                <Clock className="w-4 h-4 text-orange-400" /> Orari di Apertura
              </h4>
              <p className="text-slate-500 text-xs mb-2">Seleziona i giorni e gli orari di apertura della sede</p>
              {openingHours.map((h) => (
                <div key={h.id} className="flex items-center gap-2 mb-2">
                  <select value={h.day} onChange={e => updateOpeningHour(h.id, 'day', e.target.value)} className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:border-emerald-500 focus:outline-none">
                    {GIORNI.map(g => <option key={g} value={g}>{g}</option>)}
                  </select>
                  <input type="time" value={h.open} onChange={e => updateOpeningHour(h.id, 'open', e.target.value)} className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:border-emerald-500 focus:outline-none" />
                  <span className="text-slate-500">—</span>
                  <input type="time" value={h.close} onChange={e => updateOpeningHour(h.id, 'close', e.target.value)} className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:border-emerald-500 focus:outline-none" />
                  <button onClick={() => removeOpeningHour(h.id)} className="p-1.5 hover:bg-red-500/20 rounded-lg text-red-400"><X className="w-4 h-4" /></button>
                </div>
              ))}
              <button onClick={addOpeningHour} className="flex items-center gap-2 text-emerald-400 hover:text-emerald-300 text-sm"><Plus className="w-4 h-4" /> Aggiungi giorno</button>

              <div className="pt-4">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input type="checkbox" checked={hasVat} onChange={e => setHasVat(e.target.checked)} className="w-4 h-4 rounded border-slate-600 text-emerald-500 focus:ring-emerald-500 bg-slate-800" />
                  <span className="text-white font-semibold flex items-center gap-2"><FileText className="w-4 h-4 text-cyan-400" /> Ho la Partita IVA</span>
                </label>
              </div>
              {hasVat && (
                <>
                  <p className="text-slate-500 text-xs mb-2">Dati per la fatturazione elettronica</p>
                  <div className="grid md:grid-cols-2 gap-4">
                    <FormInput label="Partita IVA" value={formData.vat_number || ''} onChange={e => setFormData({...formData, vat_number: e.target.value})} placeholder="Es. IT12345678901" />
                    <FormInput label="Codice Fiscale" value={formData.fiscal_code || ''} onChange={e => setFormData({...formData, fiscal_code: e.target.value})} placeholder="Es. RSSMRA80A01H501Z" />
                    <FormInput label="Ragione Sociale" value={formData.legal_name || ''} onChange={e => setFormData({...formData, legal_name: e.target.value})} placeholder="Denominazione legale dell'azienda" />
                    <FormInput label="Codice SDI" value={formData.sdi_code || ''} onChange={e => setFormData({...formData, sdi_code: e.target.value})} placeholder="Codice destinatario (7 caratteri)" />
                    <FormInput label="PEC" type="email" value={formData.pec || ''} onChange={e => setFormData({...formData, pec: e.target.value})} placeholder="pec@esempio.it" />
                    <FormInput label="Regime Fiscale" value={formData.tax_regime || ''} onChange={e => setFormData({...formData, tax_regime: e.target.value})} placeholder="Es. Ordinario, Forfettario..." />
                  </div>
                </>
              )}

              <h4 className="text-white font-semibold pt-4 flex items-center gap-2">
                <CreditCard className="w-4 h-4 text-blue-400" /> Dati Pagamento
              </h4>
              <p className="text-slate-500 text-xs mb-2">Coordinate bancarie per accrediti e pagamenti</p>
              <div className="grid md:grid-cols-2 gap-4">
                <FormInput label="IBAN" value={formData.iban || ''} onChange={e => setFormData({...formData, iban: e.target.value})} placeholder="IT00X0000000000000000000000" />
                <FormInput label="Intestatario Conto" value={formData.account_holder || ''} onChange={e => setFormData({...formData, account_holder: e.target.value})} placeholder="Nome intestatario del conto" />
              </div>
            </>
          )}

          {/* STEP 2: Selezione Moduli */}
          {configStep === 2 && (
            <>
              <h4 className="text-white font-semibold mb-4">Quali servizi vuole offrire l'attività?</h4>
              <p className="text-slate-400 text-sm mb-6">Seleziona uno o più moduli da attivare. Per ogni modulo selezionato potrai configurare i dettagli nel passo successivo.</p>
              
              <div className="grid md:grid-cols-2 gap-4">
                {/* Appointments Module */}
                <div 
                  onClick={() => toggleModule('appointments')}
                  className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                    selectedModules.includes('appointments') 
                      ? 'border-emerald-500 bg-emerald-500/10' 
                      : 'border-slate-700 bg-slate-800/50 hover:border-slate-600'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${selectedModules.includes('appointments') ? 'bg-emerald-500' : 'bg-slate-700'}`}>
                      <Calendar className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1">
                      <h5 className="text-white font-semibold">Servizi a Prenotazione</h5>
                      <p className="text-slate-400 text-sm mt-1">Prenotazioni con slot orari. Ideale per parrucchieri, estetiste, medici, consulenti.</p>
                    </div>
                    {selectedModules.includes('appointments') && <CheckCircle className="w-6 h-6 text-emerald-400" />}
                  </div>
                </div>

                {/* E-commerce Module */}
                <div 
                  onClick={() => toggleModule('ecommerce')}
                  className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                    selectedModules.includes('ecommerce') 
                      ? 'border-emerald-500 bg-emerald-500/10' 
                      : 'border-slate-700 bg-slate-800/50 hover:border-slate-600'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${selectedModules.includes('ecommerce') ? 'bg-emerald-500' : 'bg-slate-700'}`}>
                      <Package className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1">
                      <h5 className="text-white font-semibold">E-commerce / Prodotti</h5>
                      <p className="text-slate-400 text-sm mt-1">Vendita prodotti con spedizione, ritiro in sede o consegna locale.</p>
                    </div>
                    {selectedModules.includes('ecommerce') && <CheckCircle className="w-6 h-6 text-emerald-400" />}
                  </div>
                </div>

                {/* Courses Module */}
                <div 
                  onClick={() => toggleModule('courses')}
                  className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                    selectedModules.includes('courses') 
                      ? 'border-emerald-500 bg-emerald-500/10' 
                      : 'border-slate-700 bg-slate-800/50 hover:border-slate-600'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${selectedModules.includes('courses') ? 'bg-emerald-500' : 'bg-slate-700'}`}>
                      <FileText className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1">
                      <h5 className="text-white font-semibold">Corsi</h5>
                      <p className="text-slate-400 text-sm mt-1">Corsi con programmazione settimanale. Yoga, fitness, formazione, etc.</p>
                    </div>
                    {selectedModules.includes('courses') && <CheckCircle className="w-6 h-6 text-emerald-400" />}
                  </div>
                </div>

                {/* Events Module */}
                <div 
                  onClick={() => toggleModule('events')}
                  className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                    selectedModules.includes('events') 
                      ? 'border-emerald-500 bg-emerald-500/10' 
                      : 'border-slate-700 bg-slate-800/50 hover:border-slate-600'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${selectedModules.includes('events') ? 'bg-emerald-500' : 'bg-slate-700'}`}>
                      <Calendar className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1">
                      <h5 className="text-white font-semibold">Eventi</h5>
                      <p className="text-slate-400 text-sm mt-1">Eventi singoli con data e ora. Workshop, serate, degustazioni.</p>
                    </div>
                    {selectedModules.includes('events') && <CheckCircle className="w-6 h-6 text-emerald-400" />}
                  </div>
                </div>
              </div>

              {wizardErrors.modules && (
                <div className="mt-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-red-400" />
                  <span className="text-red-400 text-sm">{wizardErrors.modules}</span>
                </div>
              )}

            </>
          )}

          {/* STEP 3+: SERVIZI A PRENOTAZIONE */}
          {configStep > 2 && getCurrentModuleStep() === 'appointments' && (
            <>
              <h4 className="text-white font-semibold mb-2 flex items-center gap-2"><Calendar className="w-5 h-5 text-emerald-400" /> Configura Servizi a Prenotazione</h4>
              <div className="p-4 bg-slate-800/50 rounded-xl border border-slate-700 mb-4">
                <h5 className="text-white font-semibold mb-2 flex items-center gap-2"><Users className="w-4 h-4 text-emerald-400" /> Staff</h5>
                <p className="text-slate-500 text-xs mb-3">Inserisci le persone/risorse che erogano i servizi con i relativi orari lavorativi.</p>
                {configStaff.map((staff) => (
                  <div key={staff.id} className="p-3 bg-slate-900/50 rounded-lg border border-slate-700 mb-3">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="flex-1">
                        <label className="text-xs text-slate-500 mb-1 block">Nome e Cognome</label>
                        <input type="text" placeholder="Es. Marco Bianchi" value={staff.name} onChange={e => updateStaff(staff.id, 'name', e.target.value)} className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:border-emerald-500 focus:outline-none" />
                      </div>
                      <button onClick={() => removeStaff(staff.id)} className="p-1.5 hover:bg-red-500/20 rounded-lg text-red-400 mt-5"><Trash2 className="w-4 h-4" /></button>
                    </div>
                    <label className="text-xs text-slate-500 mb-1 block">Orari lavorativi</label>
                    {staff.workingHours.map(wh => (
                      <div key={wh.id} className="flex items-center gap-2 mb-2">
                        <select value={wh.day} onChange={e => updateStaffHour(staff.id, wh.id, 'day', e.target.value)} className="bg-slate-800 border border-slate-700 rounded-lg px-2 py-1.5 text-white text-xs focus:border-emerald-500 focus:outline-none">
                          {GIORNI.map(g => <option key={g} value={g}>{g}</option>)}
                        </select>
                        <input type="time" value={wh.start} onChange={e => updateStaffHour(staff.id, wh.id, 'start', e.target.value)} className="bg-slate-800 border border-slate-700 rounded-lg px-2 py-1.5 text-white text-xs focus:border-emerald-500 focus:outline-none" />
                        <span className="text-slate-500 text-xs">—</span>
                        <input type="time" value={wh.end} onChange={e => updateStaffHour(staff.id, wh.id, 'end', e.target.value)} className="bg-slate-800 border border-slate-700 rounded-lg px-2 py-1.5 text-white text-xs focus:border-emerald-500 focus:outline-none" />
                        <button onClick={() => removeStaffHour(staff.id, wh.id)} className="p-1 hover:bg-red-500/20 rounded text-red-400"><X className="w-3 h-3" /></button>
                      </div>
                    ))}
                    <button onClick={() => addStaffHour(staff.id)} className="flex items-center gap-1 text-emerald-400 hover:text-emerald-300 text-xs"><Plus className="w-3 h-3" /> Aggiungi fascia oraria</button>
                  </div>
                ))}
                <button onClick={addStaff} className="flex items-center gap-2 text-emerald-400 hover:text-emerald-300 text-sm mt-2"><Plus className="w-4 h-4" /> Aggiungi membro staff</button>
              </div>
              <h5 className="text-white font-semibold mb-2">Elenco Servizi</h5>
              {configServices.map((service, idx) => (
                <div key={service.id} className="p-4 bg-slate-800/50 rounded-xl border border-slate-700 mb-3">
                  <div className="flex items-center justify-between mb-3"><span className="text-white font-medium">Servizio #{idx + 1}</span><button onClick={() => removeService(service.id)} className="p-1.5 hover:bg-red-500/20 rounded-lg text-red-400"><Trash2 className="w-4 h-4" /></button></div>
                  <div className="grid md:grid-cols-2 gap-3 mb-3">
                    <div><label className="text-xs text-slate-500 mb-1 block">Nome servizio <span className="text-red-400">*</span></label><input type="text" placeholder="Es. Taglio uomo, Piega donna..." value={service.name} onChange={e => updateService(service.id, 'name', e.target.value)} className={`w-full bg-slate-900 border rounded-lg px-3 py-2 text-white text-sm focus:outline-none ${wizardErrors[`service_name_${idx}`] ? 'border-red-500' : 'border-slate-700 focus:border-emerald-500'}`} />{wizardErrors[`service_name_${idx}`] && <p className="text-red-400 text-xs mt-1">{wizardErrors[`service_name_${idx}`]}</p>}</div>
                    <div><label className="text-xs text-slate-500 mb-1 block">Durata (minuti)</label><input type="number" placeholder="30" value={service.duration} onChange={e => updateService(service.id, 'duration', parseInt(e.target.value) || 0)} className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:border-emerald-500 focus:outline-none" /></div>
                  </div>
                  <div className="grid md:grid-cols-2 gap-3 mb-3">
                    <div><label className="text-xs text-slate-500 mb-1 block">Tipo prezzo</label><select value={service.priceType} onChange={e => updateService(service.id, 'priceType', e.target.value)} className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:border-emerald-500 focus:outline-none"><option value="fixed">Prezzo fisso</option><option value="variable">Prezzo variabile (min - max)</option><option value="from">A partire da</option></select></div>
                    {service.priceType === 'fixed' && <div><label className="text-xs text-slate-500 mb-1 block">Prezzo (€)</label><div className="flex items-center gap-2"><span className="text-slate-400 text-sm">€</span><input type="number" placeholder="25.00" value={service.price} onChange={e => updateService(service.id, 'price', parseFloat(e.target.value) || 0)} className="flex-1 bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:border-emerald-500 focus:outline-none" /></div></div>}
                    {service.priceType === 'variable' && <div className="flex gap-2 items-end"><div className="flex-1"><label className="text-xs text-slate-500 mb-1 block">Da (€)</label><input type="number" placeholder="Min" value={service.priceMin || ''} onChange={e => updateService(service.id, 'priceMin', parseFloat(e.target.value) || 0)} className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:border-emerald-500 focus:outline-none" /></div><div className="flex-1"><label className="text-xs text-slate-500 mb-1 block">A (€)</label><input type="number" placeholder="Max" value={service.priceMax || ''} onChange={e => updateService(service.id, 'priceMax', parseFloat(e.target.value) || 0)} className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:border-emerald-500 focus:outline-none" /></div></div>}
                    {service.priceType === 'from' && <div><label className="text-xs text-slate-500 mb-1 block">A partire da (€)</label><div className="flex items-center gap-2"><span className="text-slate-400 text-sm">€</span><input type="number" placeholder="20.00" value={service.price} onChange={e => updateService(service.id, 'price', parseFloat(e.target.value) || 0)} className="flex-1 bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:border-emerald-500 focus:outline-none" /></div></div>}
                  </div>
                  <div><label className="text-xs text-slate-500 mb-1 block">Risorse / Operatori</label><input type="text" placeholder="Es. Marco, Sala 1, Lettino A, Macchina laser..." value={service.operators_text || ''} onChange={e => updateService(service.id, 'operators_text', e.target.value)} className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:border-emerald-500 focus:outline-none" /><p className="text-slate-600 text-xs mt-1">Persone, sale, macchinari o postazioni che erogano questo servizio</p></div>
                </div>
              ))}
              <button onClick={addService} className="w-full p-3 border-2 border-dashed border-slate-700 rounded-xl text-slate-400 hover:border-emerald-500 hover:text-emerald-400 transition-colors flex items-center justify-center gap-2"><Plus className="w-5 h-5" /> Aggiungi Servizio</button>
            </>
          )}

          {/* STEP 3+: E-COMMERCE */}
          {configStep > 2 && getCurrentModuleStep() === 'ecommerce' && (
            <>
              <h4 className="text-white font-semibold mb-2 flex items-center gap-2"><Package className="w-5 h-5 text-amber-400" /> Configura Prodotti E-commerce</h4>
              <p className="text-slate-400 text-sm mb-4">Inserisci i prodotti in vendita con prezzo, disponibilità e varianti.</p>
              {configProducts.map((product, idx) => (
                <div key={product.id} className="p-4 bg-slate-800/50 rounded-xl border border-slate-700 mb-3">
                  <div className="flex items-center justify-between mb-3"><span className="text-white font-medium">Prodotto #{idx + 1}</span><button onClick={() => removeProduct(product.id)} className="p-1.5 hover:bg-red-500/20 rounded-lg text-red-400"><Trash2 className="w-4 h-4" /></button></div>
                  <div className="grid md:grid-cols-2 gap-3 mb-3">
                    <div><label className="text-xs text-slate-500 mb-1 block">Nome prodotto <span className="text-red-400">*</span></label><input type="text" placeholder="Es. Shampoo Professionale..." value={product.name} onChange={e => updateProduct(product.id, 'name', e.target.value)} className={`w-full bg-slate-900 border rounded-lg px-3 py-2 text-white text-sm focus:outline-none ${wizardErrors[`product_name_${idx}`] ? 'border-red-500' : 'border-slate-700 focus:border-emerald-500'}`} />{wizardErrors[`product_name_${idx}`] && <p className="text-red-400 text-xs mt-1">{wizardErrors[`product_name_${idx}`]}</p>}</div>
                    <div><label className="text-xs text-slate-500 mb-1 block">Categoria</label><input type="text" placeholder="Es. Cosmetici..." value={product.category} onChange={e => updateProduct(product.id, 'category', e.target.value)} className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:border-emerald-500 focus:outline-none" /></div>
                  </div>
                  <div className="grid md:grid-cols-3 gap-3">
                    <div><label className="text-xs text-slate-500 mb-1 block">Prezzo (€)</label><div className="flex items-center gap-2"><span className="text-slate-400 text-sm">€</span><input type="number" placeholder="19.90" value={product.price} onChange={e => updateProduct(product.id, 'price', parseFloat(e.target.value) || 0)} className="flex-1 bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:border-emerald-500 focus:outline-none" /></div></div>
                    <div><label className="text-xs text-slate-500 mb-1 block">Quantità</label><input type="number" placeholder="50" value={product.quantity} onChange={e => updateProduct(product.id, 'quantity', parseInt(e.target.value) || 0)} className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:border-emerald-500 focus:outline-none" /></div>
                    <div><label className="text-xs text-slate-500 mb-1 block">Varianti</label><input type="text" placeholder="Es. S, M, L" value={product.variants} onChange={e => updateProduct(product.id, 'variants', e.target.value)} className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:border-emerald-500 focus:outline-none" /></div>
                  </div>
                </div>
              ))}
              <button onClick={addProduct} className="w-full p-3 border-2 border-dashed border-slate-700 rounded-xl text-slate-400 hover:border-emerald-500 hover:text-emerald-400 transition-colors flex items-center justify-center gap-2"><Plus className="w-5 h-5" /> Aggiungi Prodotto</button>
            </>
          )}

          {/* STEP 3+: CORSI + ABBONAMENTI */}
          {configStep > 2 && getCurrentModuleStep() === 'courses' && (
            <>
              <h4 className="text-white font-semibold mb-2 flex items-center gap-2"><FileText className="w-5 h-5 text-blue-400" /> Configura Corsi</h4>
              <div className="p-4 bg-slate-800/50 rounded-xl border border-slate-700 mb-4">
                <h5 className="text-white font-semibold mb-2 flex items-center gap-2"><CreditCard className="w-4 h-4 text-blue-400" /> Abbonamenti</h5>
                <p className="text-slate-500 text-xs mb-3">Definisci i piani di abbonamento. Potrai collegare ogni corso ad un abbonamento.</p>
                {configSubscriptions.map((sub) => (
                  <div key={sub.id} className="flex items-center gap-2 mb-2">
                    <input type="text" placeholder="Nome abbonamento" value={sub.name} onChange={e => updateSubscription(sub.id, 'name', e.target.value)} className="flex-1 bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:border-emerald-500 focus:outline-none" />
                    <div className="flex items-center gap-1"><span className="text-slate-400 text-sm">€</span><input type="number" placeholder="Prezzo" value={sub.price || ''} onChange={e => updateSubscription(sub.id, 'price', parseFloat(e.target.value) || 0)} className="w-20 bg-slate-900 border border-slate-700 rounded-lg px-2 py-2 text-white text-sm focus:border-emerald-500 focus:outline-none" /></div>
                    <select value={sub.duration} onChange={e => updateSubscription(sub.id, 'duration', e.target.value)} className="bg-slate-900 border border-slate-700 rounded-lg px-2 py-2 text-white text-sm focus:border-emerald-500 focus:outline-none"><option value="1 settimana">1 settimana</option><option value="1 mese">1 mese</option><option value="3 mesi">3 mesi</option><option value="6 mesi">6 mesi</option><option value="1 anno">1 anno</option></select>
                    <button onClick={() => removeSubscription(sub.id)} className="p-1.5 hover:bg-red-500/20 rounded-lg text-red-400"><X className="w-4 h-4" /></button>
                  </div>
                ))}
                <button onClick={addSubscription} className="flex items-center gap-2 text-emerald-400 hover:text-emerald-300 text-sm mt-2"><Plus className="w-4 h-4" /> Aggiungi abbonamento</button>
              </div>
              {configCourses.map((course, idx) => (
                <div key={course.id} className="p-4 bg-slate-800/50 rounded-xl border border-slate-700 mb-3">
                  <div className="flex items-center justify-between mb-3"><span className="text-white font-medium">Corso #{idx + 1}</span><button onClick={() => removeCourse(course.id)} className="p-1.5 hover:bg-red-500/20 rounded-lg text-red-400"><Trash2 className="w-4 h-4" /></button></div>
                  <div className="grid md:grid-cols-2 gap-3 mb-3">
                    <div><label className="text-xs text-slate-500 mb-1 block">Nome corso <span className="text-red-400">*</span></label><input type="text" placeholder="Es. Yoga Mattutino..." value={course.name} onChange={e => updateCourse(course.id, 'name', e.target.value)} className={`w-full bg-slate-900 border rounded-lg px-3 py-2 text-white text-sm focus:outline-none ${wizardErrors[`course_name_${idx}`] ? 'border-red-500' : 'border-slate-700 focus:border-emerald-500'}`} />{wizardErrors[`course_name_${idx}`] && <p className="text-red-400 text-xs mt-1">{wizardErrors[`course_name_${idx}`]}</p>}</div>
                    <div><label className="text-xs text-slate-500 mb-1 block">Istruttore / Docente</label><input type="text" placeholder="Es. Coach Anna..." value={course.instructor} onChange={e => updateCourse(course.id, 'instructor', e.target.value)} className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:border-emerald-500 focus:outline-none" /></div>
                  </div>
                  <div className="grid md:grid-cols-4 gap-3 mb-3">
                    <div><label className="text-xs text-slate-500 mb-1 block">Durata lezione</label><select value={course.hourlyDuration} onChange={e => updateCourse(course.id, 'hourlyDuration', e.target.value)} className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:border-emerald-500 focus:outline-none"><option value="0.5">30 min</option><option value="1">1h</option><option value="1.5">1h 30min</option><option value="2">2h</option><option value="2.5">2h 30min</option><option value="3">3h</option></select></div>
                    <div><label className="text-xs text-slate-500 mb-1 block">Giorno</label><select value={course.day} onChange={e => updateCourse(course.id, 'day', e.target.value)} className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:border-emerald-500 focus:outline-none">{GIORNI.map(g => <option key={g} value={g}>{g}</option>)}</select></div>
                    <div><label className="text-xs text-slate-500 mb-1 block">Ora inizio</label><input type="time" value={course.startTime} onChange={e => updateCourse(course.id, 'startTime', e.target.value)} className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:border-emerald-500 focus:outline-none" /></div>
                    <div><label className="text-xs text-slate-500 mb-1 block">Posti max</label><input type="number" placeholder="15" value={course.maxParticipants} onChange={e => updateCourse(course.id, 'maxParticipants', parseInt(e.target.value) || 0)} className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:border-emerald-500 focus:outline-none" /></div>
                  </div>
                  <div className="grid md:grid-cols-2 gap-3">
                    <div><label className="text-xs text-slate-500 mb-1 block">Prezzo singola lezione (€)</label><div className="flex items-center gap-2"><span className="text-slate-400 text-sm">€</span><input type="number" placeholder="15.00" value={course.price || ''} onChange={e => updateCourse(course.id, 'price', parseFloat(e.target.value) || 0)} className="flex-1 bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:border-emerald-500 focus:outline-none" /></div></div>
                    <div><label className="text-xs text-slate-500 mb-1 block">Abbonamento collegato</label><select value={course.linkedSubscription || ''} onChange={e => updateCourse(course.id, 'linkedSubscription', e.target.value)} className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:border-emerald-500 focus:outline-none"><option value="">Nessun abbonamento</option>{configSubscriptions.filter(s => s.name).map(s => <option key={s.id} value={s.id}>{s.name} — €{s.price}/{s.duration}</option>)}</select>{configSubscriptions.length === 0 && <p className="text-slate-600 text-xs mt-1">Aggiungi prima un abbonamento</p>}</div>
                  </div>
                </div>
              ))}
              <button onClick={addCourse} className="w-full p-3 border-2 border-dashed border-slate-700 rounded-xl text-slate-400 hover:border-emerald-500 hover:text-emerald-400 transition-colors flex items-center justify-center gap-2"><Plus className="w-5 h-5" /> Aggiungi Corso</button>
            </>
          )}

          {/* STEP 3+: EVENTI */}
          {configStep > 2 && getCurrentModuleStep() === 'events' && (
            <>
              <h4 className="text-white font-semibold mb-2 flex items-center gap-2"><Calendar className="w-5 h-5 text-purple-400" /> Configura Eventi</h4>
              <p className="text-slate-400 text-sm mb-4">Inserisci gli eventi con data, ora, prezzo e capienza massima.</p>
              {configEvents.map((event, idx) => (
                <div key={event.id} className="p-4 bg-slate-800/50 rounded-xl border border-slate-700 mb-3">
                  <div className="flex items-center justify-between mb-3"><span className="text-white font-medium">Evento #{idx + 1}</span><button onClick={() => removeEvent(event.id)} className="p-1.5 hover:bg-red-500/20 rounded-lg text-red-400"><Trash2 className="w-4 h-4" /></button></div>
                  <div className="grid md:grid-cols-2 gap-3 mb-3">
                    <div><label className="text-xs text-slate-500 mb-1 block">Nome evento <span className="text-red-400">*</span></label><input type="text" placeholder="Es. Workshop Ceramica..." value={event.name} onChange={e => updateEvent(event.id, 'name', e.target.value)} className={`w-full bg-slate-900 border rounded-lg px-3 py-2 text-white text-sm focus:outline-none ${wizardErrors[`event_name_${idx}`] ? 'border-red-500' : 'border-slate-700 focus:border-emerald-500'}`} />{wizardErrors[`event_name_${idx}`] && <p className="text-red-400 text-xs mt-1">{wizardErrors[`event_name_${idx}`]}</p>}</div>
                    <div><label className="text-xs text-slate-500 mb-1 block">Descrizione breve</label><input type="text" placeholder="Una serata speciale..." value={event.description || ''} onChange={e => updateEvent(event.id, 'description', e.target.value)} className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:border-emerald-500 focus:outline-none" /></div>
                  </div>
                  <div className="grid md:grid-cols-4 gap-3">
                    <div><label className="text-xs text-slate-500 mb-1 block">Data</label><input type="date" value={event.date} onChange={e => updateEvent(event.id, 'date', e.target.value)} className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:border-emerald-500 focus:outline-none" /></div>
                    <div><label className="text-xs text-slate-500 mb-1 block">Ora</label><input type="time" value={event.time} onChange={e => updateEvent(event.id, 'time', e.target.value)} className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:border-emerald-500 focus:outline-none" /></div>
                    <div><label className="text-xs text-slate-500 mb-1 block">Prezzo (€)</label><div className="flex items-center gap-2"><span className="text-slate-400 text-sm">€</span><input type="number" placeholder="0=gratis" value={event.price} onChange={e => updateEvent(event.id, 'price', parseFloat(e.target.value) || 0)} className="flex-1 bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:border-emerald-500 focus:outline-none" /></div></div>
                    <div><label className="text-xs text-slate-500 mb-1 block">Posti max</label><input type="number" placeholder="50" value={event.maxParticipants} onChange={e => updateEvent(event.id, 'maxParticipants', parseInt(e.target.value) || 0)} className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:border-emerald-500 focus:outline-none" /></div>
                  </div>
                </div>
              ))}
              <button onClick={addEvent} className="w-full p-3 border-2 border-dashed border-slate-700 rounded-xl text-slate-400 hover:border-emerald-500 hover:text-emerald-400 transition-colors flex items-center justify-center gap-2"><Plus className="w-5 h-5" /> Aggiungi Evento</button>
            </>
          )}

          {/* ULTIMO STEP: PRESENZA ONLINE */}
          {isLastStep && (
            <>
              <h4 className="text-white font-semibold flex items-center gap-2"><ExternalLink className="w-4 h-4 text-purple-400" /> Presenza Online</h4>
              <p className="text-slate-500 text-xs mb-2">Link ai profili social e web dell'attività (opzionali)</p>
              <div className="grid md:grid-cols-2 gap-4">
                <FormInput label="Sito Web" value={formData.website || ''} onChange={e => setFormData({...formData, website: e.target.value})} placeholder="https://www.miosito.it" />
                <FormInput label="Instagram" value={formData.instagram || ''} onChange={e => setFormData({...formData, instagram: e.target.value})} placeholder="@nomeutente" />
                <FormInput label="Facebook" value={formData.facebook || ''} onChange={e => setFormData({...formData, facebook: e.target.value})} placeholder="https://facebook.com/pagina" />
                <FormInput label="Google Maps" value={formData.google_maps || ''} onChange={e => setFormData({...formData, google_maps: e.target.value})} placeholder="Link alla scheda Google Maps" />
              </div>
              <FormInput label="Note Aggiuntive" type="textarea" value={formData.notes || ''} onChange={e => setFormData({...formData, notes: e.target.value})} placeholder="Informazioni aggiuntive, richieste particolari..." />
            </>
          )}
        </div>

        {/* Navigation Buttons */}
        <div className="flex justify-between items-center pt-6 border-t border-slate-700 mt-6">
          <Button 
            variant="outline" 
            onClick={() => configStep > 1 ? setConfigStep(configStep - 1) : resetConfigWizard()}
            className="flex items-center gap-2 text-white border-slate-600 hover:bg-slate-800"
            data-testid="wizard-back-btn"
          >
            {configStep === 1 ? (
              <>
                <X className="w-4 h-4" /> Annulla
              </>
            ) : (
              <>
                <ChevronLeft className="w-4 h-4" /> Indietro
              </>
            )}
          </Button>

          <span className="text-slate-500 text-sm">
            Step {configStep} di {totalSteps}
          </span>
          
          <div className="flex gap-3">
            {!isLastStep ? (
              <Button 
                onClick={handleNextStep} 
                className="bg-emerald-500 hover:bg-emerald-400 flex items-center gap-2"
                data-testid="wizard-next-btn"
              >
                Avanti <ChevronRight className="w-4 h-4" />
              </Button>
            ) : (
              <Button 
                onClick={handleSaveConfig} 
                disabled={savingConfig} 
                className="bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 flex items-center gap-2"
                data-testid="wizard-save-btn"
              >
                {savingConfig && <RefreshCw className="w-4 h-4 animate-spin" />}
                {savingConfig ? 'Salvataggio...' : 'Salva Configurazione'}
              </Button>
            )}
          </div>
        </div>
      </Modal>
    );
  };

  // ==================== MAIN RENDER ====================

  if (loading && !initialLoadComplete) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-center w-64">
          <img src={LOGOS.dark} alt="Skipres" className="h-10 mx-auto mb-6 opacity-80" />
          <div className="relative mb-4">
            <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-emerald-500 to-emerald-400 rounded-full transition-all duration-300 ease-out"
                style={{ width: `${loadingProgress}%` }}
              ></div>
            </div>
          </div>
          <p className="text-white text-lg font-medium mb-1">{loadingProgress}%</p>
          <p className="text-slate-400 text-sm">
            {loadingProgress < 20 && 'Caricamento dashboard...'}
            {loadingProgress >= 20 && loadingProgress < 35 && 'Caricamento prodotti...'}
            {loadingProgress >= 35 && loadingProgress < 55 && 'Caricamento clienti manuali...'}
            {loadingProgress >= 55 && loadingProgress < 75 && 'Caricamento configurazioni...'}
            {loadingProgress >= 75 && loadingProgress < 100 && 'Caricamento clienti Stripe...'}
            {loadingProgress >= 100 && 'Pronto!'}
          </p>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { id: 'stripe-customers', label: 'Clienti Stripe', icon: CreditCard },
    { id: 'stripe-transactions', label: 'Transazioni Stripe', icon: Receipt },
    { id: 'manual-customers', label: 'Clienti Manuali', icon: Users },
    { id: 'configurations', label: 'Configurazioni', icon: Settings },
    { id: 'products', label: 'Prodotti', icon: Package }
  ];

  // Stripe Transactions Tab - List and detail panel with SERVER-SIDE pagination
  const renderStripeTransactions = () => {
    // Sort transactions by date
    const filtered = [...stripeTransactions].sort((a, b) => {
      return sortDirection === 'desc' 
        ? new Date(b.created) - new Date(a.created)
        : new Date(a.created) - new Date(b.created);
    });
    
    // Status labels in Italian
    const statusLabels = {
      paid: 'Pagato',
      open: 'Aperto',
      draft: 'Bozza',
      uncollectible: 'Non Riscuotibile',
      void: 'Annullato'
    };
    
    const detail = stripeTransactionDetail;
    
    // Handle search with debounce
    const handleTransactionSearch = (value) => {
      setTransactionsSearchTerm(value);
      // Debounce search
      clearTimeout(window.transactionSearchTimeout);
      window.transactionSearchTimeout = setTimeout(() => {
        if (value.length >= 2) {
          searchStripeTransactions(value);
        } else if (value.length === 0) {
          loadStripeTransactions(true);
        }
      }, 500);
    };
    
    return (
      <div className="flex gap-6">
        {/* Main Table */}
        <div className={`flex-1 min-w-0 transition-all ${selectedStripeTransaction ? 'max-w-[calc(100%-400px)]' : ''}`}>
          <div className="mb-4">
            <h2 className="text-xl font-bold text-white mb-1">Transazioni Stripe</h2>
            <p className="text-slate-400 text-sm">
              {transactionsPagination.searchMode 
                ? `Risultati ricerca: ${stripeTransactions.length} transazioni` 
                : `${stripeTransactions.length} transazioni caricate${transactionsPagination.hasMore ? ' (scorri per caricarne altre)' : ''}`}
            </p>
          </div>
          
          <div className="flex flex-wrap gap-3 items-center mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input 
                type="text" 
                value={transactionsSearchTerm} 
                onChange={e => handleTransactionSearch(e.target.value)} 
                placeholder="Cerca cliente per email o nome (min 2 caratteri)..." 
                className="w-full bg-slate-800/50 border border-slate-700 rounded-lg pl-10 pr-4 py-2.5 text-white text-sm focus:border-emerald-500 focus:outline-none" 
              />
              {transactionsPagination.loading && (
                <RefreshCw className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-emerald-400 animate-spin" />
              )}
            </div>
          </div>

          <div className="bg-slate-800/30 border border-slate-700 rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-800/50">
                  <tr>
                    <th className="text-left text-slate-400 text-xs font-medium px-4 py-3 uppercase tracking-wider">Numero</th>
                    <th className="text-left text-slate-400 text-xs font-medium px-4 py-3 uppercase tracking-wider">Cliente</th>
                    <th className="text-left text-slate-400 text-xs font-medium px-4 py-3 uppercase tracking-wider">Nome Azienda</th>
                    <th className="text-left text-slate-400 text-xs font-medium px-4 py-3 uppercase tracking-wider">Prodotto</th>
                    <th className="text-left text-slate-400 text-xs font-medium px-4 py-3 uppercase tracking-wider">Importo</th>
                    <th className="text-left text-slate-400 text-xs font-medium px-4 py-3 uppercase tracking-wider">Stato</th>
                    <th className="text-left text-slate-400 text-xs font-medium px-4 py-3 uppercase tracking-wider cursor-pointer" onClick={() => { setSortField('created'); setSortDirection(d => d === 'asc' ? 'desc' : 'asc'); }}>
                      <span className="flex items-center gap-1">Data {sortField === 'created' && <ArrowUpDown className="w-3 h-3" />}</span>
                    </th>
                    <th className="text-right text-slate-400 text-xs font-medium px-4 py-3 uppercase tracking-wider">Azioni</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700/50">
                  {transactionsPagination.loading && stripeTransactions.length === 0 ? (
                    <tr><td colSpan="8" className="px-4 py-8 text-center text-slate-400">
                      <RefreshCw className="w-5 h-5 animate-spin mx-auto mb-2" />
                      Caricamento transazioni...
                    </td></tr>
                  ) : filtered.length === 0 ? (
                    <tr><td colSpan="8" className="px-4 py-8 text-center text-slate-400">
                      {transactionsSearchTerm ? 'Nessuna transazione trovata per questa ricerca' : 'Nessuna transazione'}
                    </td></tr>
                  ) : filtered.map(transaction => (
                    <tr 
                      key={transaction.id} 
                      onClick={() => { setSelectedStripeTransaction(transaction.id); }}
                      className={`hover:bg-slate-800/30 cursor-pointer transition-colors ${selectedStripeTransaction === transaction.id ? 'bg-emerald-500/10' : ''}`}
                    >
                      <td className="px-4 py-3">
                        <span className="text-slate-300 font-mono text-sm">{transaction.number || transaction.id.slice(-8)}</span>
                      </td>
                      <td className="px-4 py-3">
                        <div>
                          <p className="text-white font-medium text-sm">{transaction.customer_name || 'N/A'}</p>
                          <p className="text-slate-400 text-xs">{transaction.customer_email}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-emerald-400 text-sm font-medium">
                          {stripeCustomersLocal[transaction.customer_id]?.company_name || <span className="text-slate-500 italic">-</span>}
                        </p>
                      </td>
                      <td className="px-4 py-3">
                        <div>
                          <p className="text-white text-sm">{transaction.product_name || 'N/A'}</p>
                          {transaction.plan_interval && (
                            <span className="text-xs text-slate-500">
                              {transaction.plan_interval === 'month' ? 'Mensile' : transaction.plan_interval === 'year' ? 'Annuale' : 'Una tantum'}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-emerald-400 font-semibold">€{transaction.amount?.toFixed(2)}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          transaction.status === 'paid' ? 'bg-emerald-500/20 text-emerald-400' :
                          transaction.status === 'open' ? 'bg-yellow-500/20 text-yellow-400' :
                          transaction.status === 'draft' ? 'bg-slate-500/20 text-slate-400' :
                          'bg-red-500/20 text-red-400'
                        }`}>
                          {statusLabels[transaction.status] || transaction.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-slate-300 text-sm">
                        {formatDate(transaction.created)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          {transaction.invoice_pdf && (
                            <a href={transaction.invoice_pdf} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} className="text-slate-400 hover:text-white p-1">
                              <FileText className="w-4 h-4" />
                            </a>
                          )}
                          {transaction.hosted_invoice_url && (
                            <a href={transaction.hosted_invoice_url} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} className="text-slate-400 hover:text-emerald-400 p-1">
                              <ExternalLink className="w-4 h-4" />
                            </a>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* Load More Button for pagination */}
            {transactionsPagination.hasMore && !transactionsPagination.searchMode && (
              <div className="p-4 border-t border-slate-700/50 text-center">
                <Button 
                  onClick={() => loadStripeTransactions(false)} 
                  disabled={transactionsPagination.loading}
                  variant="outline"
                  className="border-slate-600 text-slate-300 hover:text-white"
                >
                  {transactionsPagination.loading ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Caricamento...
                    </>
                  ) : (
                    <>
                      Carica altre transazioni
                    </>
                  )}
                </Button>
              </div>
            )}
          </div>
          <p className="text-slate-500 text-xs mt-2">
            Visualizzate: {filtered.length} transazioni
            {transactionsPagination.searchMode && ' (risultati ricerca)'}
          </p>
        </div>

        {/* Detail Panel (Right Sidebar) */}
        {selectedStripeTransaction && (
          <div className="w-[380px] flex-shrink-0">
            <div className="bg-slate-800/50 border border-slate-700 rounded-xl sticky top-20">
              <div className="p-4 border-b border-slate-700 flex items-center justify-between">
                <h3 className="text-white font-semibold">Dettagli Transazione</h3>
                <button onClick={() => { setSelectedStripeTransaction(null); setStripeTransactionDetail(null); }} className="text-slate-400 hover:text-white">
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              {loadingTransactionDetail ? (
                <div className="p-6 text-center text-slate-400">Caricamento...</div>
              ) : detail ? (
                <div className="p-4 space-y-4 max-h-[70vh] overflow-y-auto">
                  {/* Invoice Info */}
                  <div className="bg-slate-900/50 rounded-lg p-3">
                    <div className="flex items-center gap-2 text-emerald-400 mb-2">
                      <Receipt className="w-4 h-4" />
                      <span className="font-medium">Fattura</span>
                    </div>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-slate-400">Numero</span>
                        <span className="text-white font-mono">{detail.invoice?.number || 'N/A'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-400">Stato</span>
                        <span className={`font-medium ${detail.invoice?.status === 'paid' ? 'text-emerald-400' : detail.invoice?.status === 'open' ? 'text-yellow-400' : 'text-slate-300'}`}>
                          {statusLabels[detail.invoice?.status] || detail.invoice?.status}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-400">Importo Dovuto</span>
                        <span className="text-white">€{detail.invoice?.amount_due?.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-400">Importo Pagato</span>
                        <span className="text-emerald-400 font-semibold">€{detail.invoice?.amount_paid?.toFixed(2)}</span>
                      </div>
                      {detail.invoice?.amount_remaining > 0 && (
                        <div className="flex justify-between">
                          <span className="text-slate-400">Rimanente</span>
                          <span className="text-red-400">€{detail.invoice?.amount_remaining?.toFixed(2)}</span>
                        </div>
                      )}
                      <div className="flex justify-between">
                        <span className="text-slate-400">Data Creazione</span>
                        <span className="text-white">{formatDate(detail.invoice?.created)}</span>
                      </div>
                      {detail.invoice?.paid_at && (
                        <div className="flex justify-between">
                          <span className="text-slate-400">Data Pagamento</span>
                          <span className="text-white">{formatDate(detail.invoice?.paid_at)}</span>
                        </div>
                      )}
                      {detail.invoice?.billing_reason && (
                        <div className="flex justify-between">
                          <span className="text-slate-400">Motivo</span>
                          <span className="text-slate-300 capitalize">{detail.invoice?.billing_reason.replace(/_/g, ' ')}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Customer Info */}
                  <div className="bg-slate-900/50 rounded-lg p-3">
                    <div className="flex items-center gap-2 text-blue-400 mb-2">
                      <User className="w-4 h-4" />
                      <span className="font-medium">Cliente</span>
                    </div>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-slate-400">Nome</span>
                        <span className="text-white">{detail.customer?.name || 'N/A'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-400">Email</span>
                        <span className="text-white">{detail.customer?.email || 'N/A'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-400">ID</span>
                        <span className="text-slate-500 text-xs font-mono">{detail.customer?.id?.slice(-12)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Subscription/Product Info */}
                  {detail.subscription && (
                    <div className="bg-slate-900/50 rounded-lg p-3">
                      <div className="flex items-center gap-2 text-purple-400 mb-2">
                        <Package className="w-4 h-4" />
                        <span className="font-medium">Abbonamento</span>
                      </div>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-slate-400">Prodotto</span>
                          <span className="text-white">{detail.subscription?.product_name || 'N/A'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-400">Piano</span>
                          <span className="text-white">
                            €{detail.subscription?.plan_amount?.toFixed(2)}/{detail.subscription?.plan_interval === 'month' ? 'mese' : 'anno'}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Line Items */}
                  {detail.line_items && detail.line_items.length > 0 && (
                    <div className="bg-slate-900/50 rounded-lg p-3">
                      <div className="flex items-center gap-2 text-orange-400 mb-2">
                        <FileText className="w-4 h-4" />
                        <span className="font-medium">Voci</span>
                      </div>
                      <div className="space-y-2">
                        {detail.line_items.map((item, idx) => (
                          <div key={idx} className="text-sm border-b border-slate-700/50 pb-2 last:border-0 last:pb-0">
                            <p className="text-white">{item.description}</p>
                            <div className="flex justify-between text-xs mt-1">
                              <span className="text-slate-400">Qta: {item.quantity}</span>
                              <span className="text-emerald-400">€{item.amount?.toFixed(2)}</span>
                            </div>
                            {item.period_start && item.period_end && (
                              <p className="text-slate-500 text-xs mt-1">
                                Periodo: {formatDate(item.period_start)} - {formatDate(item.period_end)}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Payment Method */}
                  {detail.payment_method && (
                    <div className="bg-slate-900/50 rounded-lg p-3">
                      <div className="flex items-center gap-2 text-cyan-400 mb-2">
                        <CreditCard className="w-4 h-4" />
                        <span className="font-medium">Metodo di Pagamento</span>
                      </div>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-slate-400">Tipo</span>
                          <span className="text-white capitalize">{detail.payment_method?.brand || detail.payment_method?.type}</span>
                        </div>
                        {detail.payment_method?.last4 && (
                          <div className="flex justify-between">
                            <span className="text-slate-400">Carta</span>
                            <span className="text-white">•••• {detail.payment_method?.last4}</span>
                          </div>
                        )}
                        {detail.payment_method?.exp_month && (
                          <div className="flex justify-between">
                            <span className="text-slate-400">Scadenza</span>
                            <span className="text-white">{detail.payment_method?.exp_month}/{detail.payment_method?.exp_year}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex gap-2 pt-2">
                    {detail.invoice?.invoice_pdf && (
                      <a href={detail.invoice.invoice_pdf} target="_blank" rel="noopener noreferrer" className="flex-1">
                        <Button variant="outline" className="w-full border-slate-600 text-slate-300 hover:text-white">
                          <FileText className="w-4 h-4 mr-2" /> Scarica PDF
                        </Button>
                      </a>
                    )}
                    {detail.invoice?.hosted_invoice_url && (
                      <a href={detail.invoice.hosted_invoice_url} target="_blank" rel="noopener noreferrer" className="flex-1">
                        <Button className="w-full bg-emerald-500 hover:bg-emerald-400">
                          <ExternalLink className="w-4 h-4 mr-2" /> Vedi su Stripe
                        </Button>
                      </a>
                    )}
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Header */}
      <header className="bg-slate-900/80 border-b border-slate-800 sticky top-0 z-40 backdrop-blur-sm">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between">
          <img src={LOGOS.dark} alt="Skipres" className="h-8" />
          <div className="flex items-center gap-4">
            <span className="text-slate-400 text-sm hidden md:block">{user?.email}</span>
            <Button onClick={() => loadTabData(activeTab, true)} variant="outline" size="sm" className="border-slate-700 text-slate-300 hover:text-white" disabled={loadingTab}>
              <RefreshCw className={`w-4 h-4 ${loadingTab ? 'animate-spin' : ''}`} />
            </Button>
            <Button onClick={handleLogout} variant="outline" size="sm" className="border-slate-700 text-slate-300 hover:text-white"><LogOut className="w-4 h-4" /></Button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-6">
        {/* Tab loading indicator */}
        {loadingTab && (
          <div className="fixed top-16 left-0 right-0 z-50">
            <div className="h-1 bg-emerald-500/20">
              <div className="h-full bg-emerald-500 animate-pulse" style={{width: '100%'}}></div>
            </div>
          </div>
        )}
        
        {error && (
          <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-xl flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-400" />
            <p className="text-red-400">{error}</p>
          </div>
        )}

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-6 bg-slate-900/50 p-2 rounded-xl">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => { setActiveTab(tab.id); setSearchTerm(''); setStatusFilter(''); setProductFilter(''); setPlanTypeFilter(''); setSelectedStripeCustomer(null); setStripeCustomerDetail(null); setSelectedStripeTransaction(null); setStripeTransactionDetail(null); setSelectedManualCustomer(null); setManualCustomerDetail(null); }}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${activeTab === tab.id ? 'bg-emerald-500 text-white' : 'text-slate-400 hover:text-white hover:bg-slate-800'}`}
            >
              <tab.icon className="w-4 h-4" />
              <span className="hidden sm:inline">{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Content */}
        {activeTab === 'dashboard' && renderDashboard()}
        {activeTab === 'stripe-customers' && renderStripeCustomers()}
        {activeTab === 'stripe-transactions' && renderStripeTransactions()}
        {activeTab === 'manual-customers' && renderManualCustomers()}
        {activeTab === 'configurations' && renderConfigurations()}
        {activeTab === 'products' && renderProducts()}
      </div>

      {/* Modals */}
      {renderStripeCustomerEditModal()}
      {renderCustomerModal()}
      {renderTransactionModal()}
      {renderConfigModal()}
    </div>
  );
};

export default AdminDashboard;
